// build() 构造调用为 ~163 字段的纯机械传参，逐参单行会产生大量噪声；本文件局部豁免
// ktlint 行长检查（仅限本 token 容器文件，非全局关闭 max_line_length）。
@file:Suppress("ktlint:standard:max-line-length")

package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.ui.runtime.element.FontStyle
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.PI

/**
 * 控件主题：基础 token 的强类型容器。
 *
 * ControlTheme 不写死任何主题配色数据 —— 基础 token 的实际数值由 DSL 资源
 * （`ui-themes` 目录下的 `.allin`）提供，编译期合并后通过 [fromLookup] 解码成本类实例。
 *
 * [defaultFallback] 是唯一允许在代码层定义数值的默认兜底实例，仅用于主题资源缺失 /
 * 加载失败时保持现有控件视觉，不参与新功能扩展。
 *
 * 本类不可变，所有字段经 [Builder] 构造。历史上由 Lombok `@Getter`
 * （`lombok.accessors.fluent = true`）生成形如 `primary()` 的 fluent 访问器；改写为
 * Kotlin 后以 `@get:JvmName` 保留无 `get` 前缀的访问器名，与渲染器现有调用风格一致。
 */
@Suppress("MagicNumber", "TooManyFunctions", "LargeClass", "LongMethod", "LongParameterList", "MaxLineLength")
class ControlTheme private constructor(
    // === 跨控件设计令牌核心 ===
    val designTokens: DesignTokens,
    // === Slot token（视觉 + 几何/状态，全 25 字段分组到 [SlotTokens]，ADR-0048）===
    // slotXxx accessor 由 holder 委托；字段顺序保持原构造序（视觉块 then 几何/状态块）。
    val slotTokens: SlotTokens,
    // === 交互阈值 token ===
    // longPressThresholdMs / doubleClickThresholdMs 被 fabric UIRuntimeScreen.kt 以方法式
    // theme.longPressThresholdMs() 消费（Kotlin 调用方），故保留 fun 访问器形态而非属性，
    // 与 Lombok 原 fluent 方法 ABI 一致；其余 token 仅被 Java 方法式消费，用 @get:JvmName 属性即可。
    private val longPressThresholdMsStored: Long,
    private val doubleClickThresholdMsStored: Long,
    // === Button 几何 token ===
    // Button 控件令牌分组到 [ButtonTokens]（ADR-0048）；按钮 accessor 由 holder 委托（textPaddingX 仍为核心共享，不在此）。
    val buttonTokens: ButtonTokens,
    // ProgressBar 控件令牌分组到 [ProgressBarTokens]（ADR-0048）；progressBarXxx accessor 由 holder 委托。
    val progressBarTokens: ProgressBarTokens,
    // ItemStack 控件令牌分组到 [ItemStackTokens]（ADR-0048）；itemStackXxx accessor 由 holder 委托。
    val itemStackTokens: ItemStackTokens,
    // Dropdown 控件令牌分组到 [DropdownTokens]（ADR-0048）；dropdownXxx accessor 由 holder 委托。
    val dropdownTokens: DropdownTokens,
    // === Tooltip token ===
    // Tooltip 控件令牌分组到 [TooltipTokens]（ADR-0048）；tooltipXxx accessor 由 holder 委托。
    val tooltipTokens: TooltipTokens,
    // === Toast 视觉 token ===
    // Toast 控件令牌（默认样式 + 命名样式表 + 解析逻辑）分组到 [ToastTokens]（ADR-0048）；accessor 由 holder 委托。
    val toastTokens: ToastTokens,
) {

    @get:JvmName("primary")
    val primary: Int get() = designTokens.primary

    @get:JvmName("onPrimary")
    val onPrimary: Int get() = designTokens.onPrimary

    @get:JvmName("primaryContainer")
    val primaryContainer: Int get() = designTokens.primaryContainer

    @get:JvmName("onPrimaryContainer")
    val onPrimaryContainer: Int get() = designTokens.onPrimaryContainer

    @get:JvmName("inversePrimary")
    val inversePrimary: Int get() = designTokens.inversePrimary

    @get:JvmName("secondary")
    val secondary: Int get() = designTokens.secondary

    @get:JvmName("onSecondary")
    val onSecondary: Int get() = designTokens.onSecondary

    @get:JvmName("secondaryContainer")
    val secondaryContainer: Int get() = designTokens.secondaryContainer

    @get:JvmName("onSecondaryContainer")
    val onSecondaryContainer: Int get() = designTokens.onSecondaryContainer

    @get:JvmName("tertiary")
    val tertiary: Int get() = designTokens.tertiary

    @get:JvmName("onTertiary")
    val onTertiary: Int get() = designTokens.onTertiary

    @get:JvmName("tertiaryContainer")
    val tertiaryContainer: Int get() = designTokens.tertiaryContainer

    @get:JvmName("onTertiaryContainer")
    val onTertiaryContainer: Int get() = designTokens.onTertiaryContainer

    @get:JvmName("error")
    val error: Int get() = designTokens.error

    @get:JvmName("onError")
    val onError: Int get() = designTokens.onError

    @get:JvmName("errorContainer")
    val errorContainer: Int get() = designTokens.errorContainer

    @get:JvmName("onErrorContainer")
    val onErrorContainer: Int get() = designTokens.onErrorContainer

    @get:JvmName("successColor")
    val successColor: Int get() = designTokens.successColor

    @get:JvmName("onSuccess")
    val onSuccess: Int get() = designTokens.onSuccess

    @get:JvmName("warningColor")
    val warningColor: Int get() = designTokens.warningColor

    @get:JvmName("onWarning")
    val onWarning: Int get() = designTokens.onWarning

    @get:JvmName("infoColor")
    val infoColor: Int get() = designTokens.infoColor

    @get:JvmName("onInfo")
    val onInfo: Int get() = designTokens.onInfo

    @get:JvmName("background")
    val background: Int get() = designTokens.background

    @get:JvmName("onBackground")
    val onBackground: Int get() = designTokens.onBackground

    @get:JvmName("surface")
    val surface: Int get() = designTokens.surface

    @get:JvmName("onSurface")
    val onSurface: Int get() = designTokens.onSurface

    @get:JvmName("surfaceVariant")
    val surfaceVariant: Int get() = designTokens.surfaceVariant

    @get:JvmName("onSurfaceVariant")
    val onSurfaceVariant: Int get() = designTokens.onSurfaceVariant

    @get:JvmName("surfaceContainerLowest")
    val surfaceContainerLowest: Int get() = designTokens.surfaceContainerLowest

    @get:JvmName("surfaceContainerLow")
    val surfaceContainerLow: Int get() = designTokens.surfaceContainerLow

    @get:JvmName("surfaceContainer")
    val surfaceContainer: Int get() = designTokens.surfaceContainer

    @get:JvmName("surfaceContainerHigh")
    val surfaceContainerHigh: Int get() = designTokens.surfaceContainerHigh

    @get:JvmName("surfaceContainerHighest")
    val surfaceContainerHighest: Int get() = designTokens.surfaceContainerHighest

    @get:JvmName("surfaceTint")
    val surfaceTint: Int get() = designTokens.surfaceTint

    @get:JvmName("inverseSurface")
    val inverseSurface: Int get() = designTokens.inverseSurface

    @get:JvmName("inverseOnSurface")
    val inverseOnSurface: Int get() = designTokens.inverseOnSurface

    @get:JvmName("outline")
    val outline: Int get() = designTokens.outline

    @get:JvmName("outlineVariant")
    val outlineVariant: Int get() = designTokens.outlineVariant

    @get:JvmName("shadow")
    val shadow: Int get() = designTokens.shadow

    @get:JvmName("scrim")
    val scrim: Int get() = designTokens.scrim

    @get:JvmName("stateLayerHoverOpacity")
    val stateLayerHoverOpacity: Float get() = designTokens.stateLayerHoverOpacity

    @get:JvmName("stateLayerFocusOpacity")
    val stateLayerFocusOpacity: Float get() = designTokens.stateLayerFocusOpacity

    @get:JvmName("stateLayerPressedOpacity")
    val stateLayerPressedOpacity: Float get() = designTokens.stateLayerPressedOpacity

    @get:JvmName("stateLayerDraggedOpacity")
    val stateLayerDraggedOpacity: Float get() = designTokens.stateLayerDraggedOpacity

    @get:JvmName("disabledOpacity")
    val disabledOpacity: Float get() = designTokens.disabledOpacity

    @get:JvmName("disabledContentOpacity")
    val disabledContentOpacity: Float get() = designTokens.disabledContentOpacity

    @get:JvmName("typographyDisplayLargeSize")
    val typographyDisplayLargeSize: Float get() = designTokens.typographyDisplayLargeSize

    @get:JvmName("typographyDisplayLargeLineHeight")
    val typographyDisplayLargeLineHeight: Float get() = designTokens.typographyDisplayLargeLineHeight

    @get:JvmName("typographyDisplayLargeLetterSpacing")
    val typographyDisplayLargeLetterSpacing: Float get() = designTokens.typographyDisplayLargeLetterSpacing

    @get:JvmName("typographyDisplayMediumSize")
    val typographyDisplayMediumSize: Float get() = designTokens.typographyDisplayMediumSize

    @get:JvmName("typographyDisplayMediumLineHeight")
    val typographyDisplayMediumLineHeight: Float get() = designTokens.typographyDisplayMediumLineHeight

    @get:JvmName("typographyDisplayMediumLetterSpacing")
    val typographyDisplayMediumLetterSpacing: Float get() = designTokens.typographyDisplayMediumLetterSpacing

    @get:JvmName("typographyDisplaySmallSize")
    val typographyDisplaySmallSize: Float get() = designTokens.typographyDisplaySmallSize

    @get:JvmName("typographyDisplaySmallLineHeight")
    val typographyDisplaySmallLineHeight: Float get() = designTokens.typographyDisplaySmallLineHeight

    @get:JvmName("typographyDisplaySmallLetterSpacing")
    val typographyDisplaySmallLetterSpacing: Float get() = designTokens.typographyDisplaySmallLetterSpacing

    @get:JvmName("typographyHeadlineLargeSize")
    val typographyHeadlineLargeSize: Float get() = designTokens.typographyHeadlineLargeSize

    @get:JvmName("typographyHeadlineLargeLineHeight")
    val typographyHeadlineLargeLineHeight: Float get() = designTokens.typographyHeadlineLargeLineHeight

    @get:JvmName("typographyHeadlineLargeLetterSpacing")
    val typographyHeadlineLargeLetterSpacing: Float get() = designTokens.typographyHeadlineLargeLetterSpacing

    @get:JvmName("typographyHeadlineMediumSize")
    val typographyHeadlineMediumSize: Float get() = designTokens.typographyHeadlineMediumSize

    @get:JvmName("typographyHeadlineMediumLineHeight")
    val typographyHeadlineMediumLineHeight: Float get() = designTokens.typographyHeadlineMediumLineHeight

    @get:JvmName("typographyHeadlineMediumLetterSpacing")
    val typographyHeadlineMediumLetterSpacing: Float get() = designTokens.typographyHeadlineMediumLetterSpacing

    @get:JvmName("typographyHeadlineSmallSize")
    val typographyHeadlineSmallSize: Float get() = designTokens.typographyHeadlineSmallSize

    @get:JvmName("typographyHeadlineSmallLineHeight")
    val typographyHeadlineSmallLineHeight: Float get() = designTokens.typographyHeadlineSmallLineHeight

    @get:JvmName("typographyHeadlineSmallLetterSpacing")
    val typographyHeadlineSmallLetterSpacing: Float get() = designTokens.typographyHeadlineSmallLetterSpacing

    @get:JvmName("typographyTitleLargeSize")
    val typographyTitleLargeSize: Float get() = designTokens.typographyTitleLargeSize

    @get:JvmName("typographyTitleLargeLineHeight")
    val typographyTitleLargeLineHeight: Float get() = designTokens.typographyTitleLargeLineHeight

    @get:JvmName("typographyTitleLargeLetterSpacing")
    val typographyTitleLargeLetterSpacing: Float get() = designTokens.typographyTitleLargeLetterSpacing

    @get:JvmName("typographyTitleMediumSize")
    val typographyTitleMediumSize: Float get() = designTokens.typographyTitleMediumSize

    @get:JvmName("typographyTitleMediumLineHeight")
    val typographyTitleMediumLineHeight: Float get() = designTokens.typographyTitleMediumLineHeight

    @get:JvmName("typographyTitleMediumLetterSpacing")
    val typographyTitleMediumLetterSpacing: Float get() = designTokens.typographyTitleMediumLetterSpacing

    @get:JvmName("typographyTitleSmallSize")
    val typographyTitleSmallSize: Float get() = designTokens.typographyTitleSmallSize

    @get:JvmName("typographyTitleSmallLineHeight")
    val typographyTitleSmallLineHeight: Float get() = designTokens.typographyTitleSmallLineHeight

    @get:JvmName("typographyTitleSmallLetterSpacing")
    val typographyTitleSmallLetterSpacing: Float get() = designTokens.typographyTitleSmallLetterSpacing

    @get:JvmName("typographyBodyLargeSize")
    val typographyBodyLargeSize: Float get() = designTokens.typographyBodyLargeSize

    @get:JvmName("typographyBodyLargeLineHeight")
    val typographyBodyLargeLineHeight: Float get() = designTokens.typographyBodyLargeLineHeight

    @get:JvmName("typographyBodyLargeLetterSpacing")
    val typographyBodyLargeLetterSpacing: Float get() = designTokens.typographyBodyLargeLetterSpacing

    @get:JvmName("typographyBodyMediumSize")
    val typographyBodyMediumSize: Float get() = designTokens.typographyBodyMediumSize

    @get:JvmName("typographyBodyMediumLineHeight")
    val typographyBodyMediumLineHeight: Float get() = designTokens.typographyBodyMediumLineHeight

    @get:JvmName("typographyBodyMediumLetterSpacing")
    val typographyBodyMediumLetterSpacing: Float get() = designTokens.typographyBodyMediumLetterSpacing

    @get:JvmName("typographyBodySmallSize")
    val typographyBodySmallSize: Float get() = designTokens.typographyBodySmallSize

    @get:JvmName("typographyBodySmallLineHeight")
    val typographyBodySmallLineHeight: Float get() = designTokens.typographyBodySmallLineHeight

    @get:JvmName("typographyBodySmallLetterSpacing")
    val typographyBodySmallLetterSpacing: Float get() = designTokens.typographyBodySmallLetterSpacing

    @get:JvmName("typographyLabelLargeSize")
    val typographyLabelLargeSize: Float get() = designTokens.typographyLabelLargeSize

    @get:JvmName("typographyLabelLargeLineHeight")
    val typographyLabelLargeLineHeight: Float get() = designTokens.typographyLabelLargeLineHeight

    @get:JvmName("typographyLabelLargeLetterSpacing")
    val typographyLabelLargeLetterSpacing: Float get() = designTokens.typographyLabelLargeLetterSpacing

    @get:JvmName("typographyLabelMediumSize")
    val typographyLabelMediumSize: Float get() = designTokens.typographyLabelMediumSize

    @get:JvmName("typographyLabelMediumLineHeight")
    val typographyLabelMediumLineHeight: Float get() = designTokens.typographyLabelMediumLineHeight

    @get:JvmName("typographyLabelMediumLetterSpacing")
    val typographyLabelMediumLetterSpacing: Float get() = designTokens.typographyLabelMediumLetterSpacing

    @get:JvmName("typographyLabelSmallSize")
    val typographyLabelSmallSize: Float get() = designTokens.typographyLabelSmallSize

    @get:JvmName("typographyLabelSmallLineHeight")
    val typographyLabelSmallLineHeight: Float get() = designTokens.typographyLabelSmallLineHeight

    @get:JvmName("typographyLabelSmallLetterSpacing")
    val typographyLabelSmallLetterSpacing: Float get() = designTokens.typographyLabelSmallLetterSpacing

    @get:JvmName("fontFamily")
    val fontFamily: String get() = designTokens.fontFamily

    @get:JvmName("cornerRadiusXSmall")
    val cornerRadiusXSmall: Float get() = designTokens.cornerRadiusXSmall

    @get:JvmName("cornerRadiusSmall")
    val cornerRadiusSmall: Float get() = designTokens.cornerRadiusSmall

    @get:JvmName("cornerRadiusMedium")
    val cornerRadiusMedium: Float get() = designTokens.cornerRadiusMedium

    @get:JvmName("cornerRadiusLarge")
    val cornerRadiusLarge: Float get() = designTokens.cornerRadiusLarge

    @get:JvmName("cornerRadiusExtraLarge")
    val cornerRadiusExtraLarge: Float get() = designTokens.cornerRadiusExtraLarge

    @get:JvmName("cornerRadiusFull")
    val cornerRadiusFull: Float get() = designTokens.cornerRadiusFull

    @get:JvmName("borderWidth")
    val borderWidth: Float get() = designTokens.borderWidth

    @get:JvmName("sliderTrackHeight")
    val sliderTrackHeight: Float get() = designTokens.sliderTrackHeight

    @get:JvmName("sliderThumbSize")
    val sliderThumbSize: Float get() = designTokens.sliderThumbSize

    @get:JvmName("stateLayerSize")
    val stateLayerSize: Float get() = designTokens.stateLayerSize

    @get:JvmName("textPaddingX")
    val textPaddingX: Float get() = designTokens.textPaddingX

    @get:JvmName("glowColor")
    val glowColor: Int get() = designTokens.glowColor

    @get:JvmName("glowIntensity")
    val glowIntensity: Float get() = designTokens.glowIntensity

    @get:JvmName("glowSize")
    val glowSize: Float get() = designTokens.glowSize

    @get:JvmName("frameStrokeColor")
    val frameStrokeColor: Int get() = designTokens.frameStrokeColor

    @get:JvmName("frameStrokeWidth")
    val frameStrokeWidth: Float get() = designTokens.frameStrokeWidth

    @get:JvmName("scanlineColor")
    val scanlineColor: Int get() = designTokens.scanlineColor

    @get:JvmName("scanlineDensity")
    val scanlineDensity: Float get() = designTokens.scanlineDensity

    @get:JvmName("animDurationEnterMs")
    val animDurationEnterMs: Long get() = designTokens.animDurationEnterMs

    @get:JvmName("animDurationExitMs")
    val animDurationExitMs: Long get() = designTokens.animDurationExitMs

    @get:JvmName("animDurationStaggerMs")
    val animDurationStaggerMs: Long get() = designTokens.animDurationStaggerMs

    /** 长按触发阈值（毫秒）。fun 访问器形态供 Kotlin 方法式调用方使用（见构造参数注释）。 */
    fun longPressThresholdMs(): Long = longPressThresholdMsStored

    /** 双击触发阈值（毫秒）。fun 访问器形态供 Kotlin 方法式调用方使用（见构造参数注释）。 */
    fun doubleClickThresholdMs(): Long = doubleClickThresholdMsStored

    // ProgressBar 控件令牌（ADR-0048）：accessor 委托内部 [progressBarTokens]，签名不变（消费者零改动）。
    @get:JvmName("progressBarNotchColor")
    val progressBarNotchColor: Int get() = progressBarTokens.notchColor

    // ItemStack 控件令牌（ADR-0048）：accessor 委托内部 [itemStackTokens]，签名不变（消费者零改动）。
    @get:JvmName("itemStackBackgroundColor")
    val itemStackBackgroundColor: Int get() = itemStackTokens.backgroundColor

    @get:JvmName("itemStackBackgroundInnerColor")
    val itemStackBackgroundInnerColor: Int get() = itemStackTokens.backgroundInnerColor

    @get:JvmName("itemStackHoverColor")
    val itemStackHoverColor: Int get() = itemStackTokens.hoverColor

    // Dropdown 控件令牌（ADR-0048）：accessor 委托内部 [dropdownTokens]，签名不变（消费者零改动）。
    @get:JvmName("dropdownOptionHoverColor")
    val dropdownOptionHoverColor: Int get() = dropdownTokens.optionHoverColor

    // Tooltip 控件令牌（ADR-0048）：accessor 委托内部 [tooltipTokens]，签名不变（消费者零改动）。
    @get:JvmName("tooltipDelayMs")
    val tooltipDelayMs: Long get() = tooltipTokens.delayMs

    @get:JvmName("tooltipBackgroundColor")
    val tooltipBackgroundColor: Int get() = tooltipTokens.backgroundColor

    @get:JvmName("tooltipTextColor")
    val tooltipTextColor: Int get() = tooltipTokens.textColor

    @get:JvmName("tooltipCornerRadius")
    val tooltipCornerRadius: Float get() = tooltipTokens.cornerRadius

    @get:JvmName("tooltipPaddingX")
    val tooltipPaddingX: Float get() = tooltipTokens.paddingX

    @get:JvmName("tooltipPaddingY")
    val tooltipPaddingY: Float get() = tooltipTokens.paddingY

    @get:JvmName("tooltipLineSpacing")
    val tooltipLineSpacing: Float get() = tooltipTokens.lineSpacing

    // Button 控件令牌（ADR-0048）：accessor 委托内部 [buttonTokens]，签名不变（消费者零改动）。
    @get:JvmName("textPaddingY")
    val textPaddingY: Float get() = buttonTokens.textPaddingY

    @get:JvmName("textBaselineY")
    val textBaselineY: Float get() = buttonTokens.textBaselineY

    @get:JvmName("buttonCornerRadius")
    val buttonCornerRadius: Float get() = buttonTokens.cornerRadius

    @get:JvmName("buttonIconSpacing")
    val buttonIconSpacing: Float get() = buttonTokens.iconSpacing

    @get:JvmName("buttonElevationDefault")
    val buttonElevationDefault: Float get() = buttonTokens.elevationDefault

    @get:JvmName("buttonElevationHover")
    val buttonElevationHover: Float get() = buttonTokens.elevationHover

    @get:JvmName("buttonElevationPressed")
    val buttonElevationPressed: Float get() = buttonTokens.elevationPressed

    @get:JvmName("buttonTextTransform")
    val buttonTextTransform: String get() = buttonTokens.textTransform

    // Slot 控件令牌（ADR-0048）：accessor 委托内部 [slotTokens]，签名不变（消费者零改动）。
    @get:JvmName("slotHoverColor")
    val slotHoverColor: Int get() = slotTokens.hoverColor

    @get:JvmName("slotPlaceholderInset")
    val slotPlaceholderInset: Float get() = slotTokens.placeholderInset

    @get:JvmName("slotRejectedShakeAmplitude")
    val slotRejectedShakeAmplitude: Float get() = slotTokens.rejectedShakeAmplitude

    @get:JvmName("slotRejectedShakeFrequency")
    val slotRejectedShakeFrequency: Float get() = slotTokens.rejectedShakeFrequency

    @get:JvmName("slotFailedMaxAlpha")
    val slotFailedMaxAlpha: Float get() = slotTokens.failedMaxAlpha

    @get:JvmName("slotFailedColor")
    val slotFailedColor: Int get() = slotTokens.failedColor

    @get:JvmName("slotSuccessMaxAlpha")
    val slotSuccessMaxAlpha: Float get() = slotTokens.successMaxAlpha

    @get:JvmName("slotSuccessColor")
    val slotSuccessColor: Int get() = slotTokens.successColor

    @get:JvmName("slotTimeoutMaxAlpha")
    val slotTimeoutMaxAlpha: Float get() = slotTokens.timeoutMaxAlpha

    @get:JvmName("slotTimeoutColor")
    val slotTimeoutColor: Int get() = slotTokens.timeoutColor

    @get:JvmName("slotQuickCraftPreviewAlpha")
    val slotQuickCraftPreviewAlpha: Float get() = slotTokens.quickCraftPreviewAlpha

    @get:JvmName("slotRejectedReplayAlpha")
    val slotRejectedReplayAlpha: Float get() = slotTokens.rejectedReplayAlpha

    @get:JvmName("slotBorderColor")
    val slotBorderColor: Int get() = slotTokens.borderColor

    @get:JvmName("slotBorderWidth")
    val slotBorderWidth: Float get() = slotTokens.borderWidth

    @get:JvmName("slotCornerRadius")
    val slotCornerRadius: Float get() = slotTokens.cornerRadius

    @get:JvmName("slotSelectedOutlineColor")
    val slotSelectedOutlineColor: Int get() = slotTokens.selectedOutlineColor

    @get:JvmName("slotSelectedOutlineWidth")
    val slotSelectedOutlineWidth: Float get() = slotTokens.selectedOutlineWidth

    @get:JvmName("slotPressedOpacity")
    val slotPressedOpacity: Float get() = slotTokens.pressedOpacity

    @get:JvmName("slotFocusOpacity")
    val slotFocusOpacity: Float get() = slotTokens.focusOpacity

    @get:JvmName("slotDisabledOpacity")
    val slotDisabledOpacity: Float get() = slotTokens.disabledOpacity

    @get:JvmName("slotHoverOpacity")
    val slotHoverOpacity: Float get() = slotTokens.hoverOpacity

    @get:JvmName("slotFocusOverlayColor")
    val slotFocusOverlayColor: Int get() = slotTokens.focusOverlayColor

    @get:JvmName("slotDisabledOverlayColor")
    val slotDisabledOverlayColor: Int get() = slotTokens.disabledOverlayColor

    @get:JvmName("slotBackgroundOuterColor")
    val slotBackgroundOuterColor: Int get() = slotTokens.backgroundOuterColor

    @get:JvmName("slotBackgroundInnerColor")
    val slotBackgroundInnerColor: Int get() = slotTokens.backgroundInnerColor

    // Toast 控件令牌（ADR-0048）：accessor 与解析委托内部 [toastTokens]，签名不变（消费者零改动）。
    @get:JvmName("defaultToastStyle")
    val defaultToastStyle: ToastStyle get() = toastTokens.defaultStyle

    @get:JvmName("toastStyles")
    val toastStyles: Map<String, ToastStyle> get() = toastTokens.styles

    /**
     * 按 name 查命名样式；不存在返回 [defaultToastStyle]。大小写敏感，表项缺失时不抛异常。
     * 解析逻辑已下沉至 [ToastTokens.resolve]（行为随数据），此处仅委托以保持对外签名不变。
     */
    fun resolveToastStyle(name: String?): ToastStyle = toastTokens.resolve(name)

    /** 派生构建器，预填当前所有字段（含命名样式表）；对应 Lombok `toBuilder = true`。 */
    fun toBuilder(): Builder = Builder()
        .primary(primary).onPrimary(onPrimary).primaryContainer(primaryContainer)
        .onPrimaryContainer(onPrimaryContainer).inversePrimary(inversePrimary)
        .secondary(secondary).onSecondary(onSecondary).secondaryContainer(secondaryContainer)
        .onSecondaryContainer(onSecondaryContainer)
        .tertiary(tertiary).onTertiary(onTertiary).tertiaryContainer(tertiaryContainer)
        .onTertiaryContainer(onTertiaryContainer)
        .error(error).onError(onError).errorContainer(errorContainer).onErrorContainer(onErrorContainer)
        .successColor(successColor).onSuccess(onSuccess).warningColor(warningColor)
        .onWarning(onWarning).infoColor(infoColor).onInfo(onInfo)
        .background(background).onBackground(onBackground).surface(surface).onSurface(onSurface)
        .surfaceVariant(surfaceVariant).onSurfaceVariant(onSurfaceVariant)
        .surfaceContainerLowest(surfaceContainerLowest).surfaceContainerLow(surfaceContainerLow)
        .surfaceContainer(surfaceContainer).surfaceContainerHigh(surfaceContainerHigh)
        .surfaceContainerHighest(surfaceContainerHighest).surfaceTint(surfaceTint)
        .inverseSurface(inverseSurface).inverseOnSurface(inverseOnSurface)
        .outline(outline).outlineVariant(outlineVariant).shadow(shadow).scrim(scrim)
        .stateLayerHoverOpacity(stateLayerHoverOpacity).stateLayerFocusOpacity(stateLayerFocusOpacity)
        .stateLayerPressedOpacity(stateLayerPressedOpacity).stateLayerDraggedOpacity(stateLayerDraggedOpacity)
        .disabledOpacity(disabledOpacity).disabledContentOpacity(disabledContentOpacity)
        .typographyDisplayLargeSize(typographyDisplayLargeSize)
        .typographyDisplayLargeLineHeight(typographyDisplayLargeLineHeight)
        .typographyDisplayLargeLetterSpacing(typographyDisplayLargeLetterSpacing)
        .typographyDisplayMediumSize(typographyDisplayMediumSize)
        .typographyDisplayMediumLineHeight(typographyDisplayMediumLineHeight)
        .typographyDisplayMediumLetterSpacing(typographyDisplayMediumLetterSpacing)
        .typographyDisplaySmallSize(typographyDisplaySmallSize)
        .typographyDisplaySmallLineHeight(typographyDisplaySmallLineHeight)
        .typographyDisplaySmallLetterSpacing(typographyDisplaySmallLetterSpacing)
        .typographyHeadlineLargeSize(typographyHeadlineLargeSize)
        .typographyHeadlineLargeLineHeight(typographyHeadlineLargeLineHeight)
        .typographyHeadlineLargeLetterSpacing(typographyHeadlineLargeLetterSpacing)
        .typographyHeadlineMediumSize(typographyHeadlineMediumSize)
        .typographyHeadlineMediumLineHeight(typographyHeadlineMediumLineHeight)
        .typographyHeadlineMediumLetterSpacing(typographyHeadlineMediumLetterSpacing)
        .typographyHeadlineSmallSize(typographyHeadlineSmallSize)
        .typographyHeadlineSmallLineHeight(typographyHeadlineSmallLineHeight)
        .typographyHeadlineSmallLetterSpacing(typographyHeadlineSmallLetterSpacing)
        .typographyTitleLargeSize(typographyTitleLargeSize)
        .typographyTitleLargeLineHeight(typographyTitleLargeLineHeight)
        .typographyTitleLargeLetterSpacing(typographyTitleLargeLetterSpacing)
        .typographyTitleMediumSize(typographyTitleMediumSize)
        .typographyTitleMediumLineHeight(typographyTitleMediumLineHeight)
        .typographyTitleMediumLetterSpacing(typographyTitleMediumLetterSpacing)
        .typographyTitleSmallSize(typographyTitleSmallSize)
        .typographyTitleSmallLineHeight(typographyTitleSmallLineHeight)
        .typographyTitleSmallLetterSpacing(typographyTitleSmallLetterSpacing)
        .typographyBodyLargeSize(typographyBodyLargeSize)
        .typographyBodyLargeLineHeight(typographyBodyLargeLineHeight)
        .typographyBodyLargeLetterSpacing(typographyBodyLargeLetterSpacing)
        .typographyBodyMediumSize(typographyBodyMediumSize)
        .typographyBodyMediumLineHeight(typographyBodyMediumLineHeight)
        .typographyBodyMediumLetterSpacing(typographyBodyMediumLetterSpacing)
        .typographyBodySmallSize(typographyBodySmallSize)
        .typographyBodySmallLineHeight(typographyBodySmallLineHeight)
        .typographyBodySmallLetterSpacing(typographyBodySmallLetterSpacing)
        .typographyLabelLargeSize(typographyLabelLargeSize)
        .typographyLabelLargeLineHeight(typographyLabelLargeLineHeight)
        .typographyLabelLargeLetterSpacing(typographyLabelLargeLetterSpacing)
        .typographyLabelMediumSize(typographyLabelMediumSize)
        .typographyLabelMediumLineHeight(typographyLabelMediumLineHeight)
        .typographyLabelMediumLetterSpacing(typographyLabelMediumLetterSpacing)
        .typographyLabelSmallSize(typographyLabelSmallSize)
        .typographyLabelSmallLineHeight(typographyLabelSmallLineHeight)
        .typographyLabelSmallLetterSpacing(typographyLabelSmallLetterSpacing)
        .fontFamily(fontFamily)
        .cornerRadiusXSmall(cornerRadiusXSmall).cornerRadiusSmall(cornerRadiusSmall)
        .cornerRadiusMedium(cornerRadiusMedium).cornerRadiusLarge(cornerRadiusLarge)
        .cornerRadiusExtraLarge(cornerRadiusExtraLarge).cornerRadiusFull(cornerRadiusFull)
        .borderWidth(borderWidth).sliderTrackHeight(sliderTrackHeight).sliderThumbSize(sliderThumbSize)
        .stateLayerSize(stateLayerSize).textPaddingX(textPaddingX)
        .slotHoverColor(slotHoverColor).slotPlaceholderInset(slotPlaceholderInset)
        .slotRejectedShakeAmplitude(slotRejectedShakeAmplitude).slotRejectedShakeFrequency(slotRejectedShakeFrequency)
        .slotFailedMaxAlpha(slotFailedMaxAlpha).slotFailedColor(slotFailedColor)
        .slotSuccessMaxAlpha(slotSuccessMaxAlpha).slotSuccessColor(slotSuccessColor)
        .slotTimeoutMaxAlpha(slotTimeoutMaxAlpha).slotTimeoutColor(slotTimeoutColor)
        .slotQuickCraftPreviewAlpha(slotQuickCraftPreviewAlpha).slotRejectedReplayAlpha(slotRejectedReplayAlpha)
        .longPressThresholdMs(longPressThresholdMs()).doubleClickThresholdMs(doubleClickThresholdMs())
        .textPaddingY(textPaddingY).textBaselineY(textBaselineY).buttonCornerRadius(buttonCornerRadius)
        .buttonIconSpacing(buttonIconSpacing).buttonElevationDefault(buttonElevationDefault)
        .buttonElevationHover(buttonElevationHover).buttonElevationPressed(buttonElevationPressed)
        .buttonTextTransform(buttonTextTransform)
        .slotBorderColor(slotBorderColor).slotBorderWidth(slotBorderWidth).slotCornerRadius(slotCornerRadius)
        .slotSelectedOutlineColor(slotSelectedOutlineColor).slotSelectedOutlineWidth(slotSelectedOutlineWidth)
        .slotPressedOpacity(slotPressedOpacity).slotFocusOpacity(slotFocusOpacity)
        .slotDisabledOpacity(slotDisabledOpacity).slotHoverOpacity(slotHoverOpacity)
        .slotFocusOverlayColor(slotFocusOverlayColor).slotDisabledOverlayColor(slotDisabledOverlayColor)
        .slotBackgroundOuterColor(slotBackgroundOuterColor).slotBackgroundInnerColor(slotBackgroundInnerColor)
        .progressBarNotchColor(progressBarNotchColor).itemStackBackgroundColor(itemStackBackgroundColor)
        .itemStackBackgroundInnerColor(itemStackBackgroundInnerColor).itemStackHoverColor(itemStackHoverColor)
        .dropdownOptionHoverColor(dropdownOptionHoverColor)
        .glowColor(glowColor).glowIntensity(glowIntensity).glowSize(glowSize)
        .frameStrokeColor(frameStrokeColor).frameStrokeWidth(frameStrokeWidth)
        .scanlineColor(scanlineColor).scanlineDensity(scanlineDensity)
        .animDurationEnterMs(animDurationEnterMs).animDurationExitMs(animDurationExitMs)
        .animDurationStaggerMs(animDurationStaggerMs)
        .tooltipDelayMs(tooltipDelayMs).tooltipBackgroundColor(tooltipBackgroundColor)
        .tooltipTextColor(tooltipTextColor).tooltipCornerRadius(tooltipCornerRadius)
        .tooltipPaddingX(tooltipPaddingX).tooltipPaddingY(tooltipPaddingY).tooltipLineSpacing(tooltipLineSpacing)
        .defaultToastStyle(defaultToastStyle).toastStyles(toastStyles)

    /**
     * 控件主题构建器：复刻 Lombok `@Builder(builderClassName = "Builder", toBuilder = true)`
     * 与 `@Singular toastStyles` 的链式 ABI。各字段默认值对应原 `@Builder.Default`；
     * 未标 `@Builder.Default` 的色族默认 0。
     */
    class Builder internal constructor() {
        private var primaryValue: Int = 0
        private var onPrimaryValue: Int = 0
        private var primaryContainerValue: Int = 0
        private var onPrimaryContainerValue: Int = 0
        private var inversePrimaryValue: Int = 0
        private var secondaryValue: Int = 0
        private var onSecondaryValue: Int = 0
        private var secondaryContainerValue: Int = 0
        private var onSecondaryContainerValue: Int = 0
        private var tertiaryValue: Int = 0
        private var onTertiaryValue: Int = 0
        private var tertiaryContainerValue: Int = 0
        private var onTertiaryContainerValue: Int = 0
        private var errorValue: Int = 0
        private var onErrorValue: Int = 0
        private var errorContainerValue: Int = 0
        private var onErrorContainerValue: Int = 0
        private var successColorValue: Int = 0
        private var onSuccessValue: Int = 0
        private var warningColorValue: Int = 0
        private var onWarningValue: Int = 0
        private var infoColorValue: Int = 0
        private var onInfoValue: Int = 0
        private var backgroundValue: Int = 0
        private var onBackgroundValue: Int = 0
        private var surfaceValue: Int = 0
        private var onSurfaceValue: Int = 0
        private var surfaceVariantValue: Int = 0
        private var onSurfaceVariantValue: Int = 0
        private var surfaceContainerLowestValue: Int = 0
        private var surfaceContainerLowValue: Int = 0
        private var surfaceContainerValue: Int = 0
        private var surfaceContainerHighValue: Int = 0
        private var surfaceContainerHighestValue: Int = 0
        private var surfaceTintValue: Int = 0
        private var inverseSurfaceValue: Int = 0
        private var inverseOnSurfaceValue: Int = 0
        private var outlineValue: Int = 0
        private var outlineVariantValue: Int = 0
        private var shadowValue: Int = 0
        private var scrimValue: Int = 0
        private var stateLayerHoverOpacityValue: Float = 0.08f
        private var stateLayerFocusOpacityValue: Float = 0.10f
        private var stateLayerPressedOpacityValue: Float = 0.10f
        private var stateLayerDraggedOpacityValue: Float = 0.12f
        private var disabledOpacityValue: Float = 0.38f
        private var disabledContentOpacityValue: Float = 0.38f
        private var typographyDisplayLargeSizeValue: Float = 9f
        private var typographyDisplayLargeLineHeightValue: Float = 9f
        private var typographyDisplayLargeLetterSpacingValue: Float = 0f
        private var typographyDisplayMediumSizeValue: Float = 9f
        private var typographyDisplayMediumLineHeightValue: Float = 9f
        private var typographyDisplayMediumLetterSpacingValue: Float = 0f
        private var typographyDisplaySmallSizeValue: Float = 9f
        private var typographyDisplaySmallLineHeightValue: Float = 9f
        private var typographyDisplaySmallLetterSpacingValue: Float = 0f
        private var typographyHeadlineLargeSizeValue: Float = 9f
        private var typographyHeadlineLargeLineHeightValue: Float = 9f
        private var typographyHeadlineLargeLetterSpacingValue: Float = 0f
        private var typographyHeadlineMediumSizeValue: Float = 9f
        private var typographyHeadlineMediumLineHeightValue: Float = 9f
        private var typographyHeadlineMediumLetterSpacingValue: Float = 0f
        private var typographyHeadlineSmallSizeValue: Float = 9f
        private var typographyHeadlineSmallLineHeightValue: Float = 9f
        private var typographyHeadlineSmallLetterSpacingValue: Float = 0f
        private var typographyTitleLargeSizeValue: Float = 9f
        private var typographyTitleLargeLineHeightValue: Float = 9f
        private var typographyTitleLargeLetterSpacingValue: Float = 0f
        private var typographyTitleMediumSizeValue: Float = 9f
        private var typographyTitleMediumLineHeightValue: Float = 9f
        private var typographyTitleMediumLetterSpacingValue: Float = 0f
        private var typographyTitleSmallSizeValue: Float = 9f
        private var typographyTitleSmallLineHeightValue: Float = 9f
        private var typographyTitleSmallLetterSpacingValue: Float = 0f
        private var typographyBodyLargeSizeValue: Float = 9f
        private var typographyBodyLargeLineHeightValue: Float = 9f
        private var typographyBodyLargeLetterSpacingValue: Float = 0f
        private var typographyBodyMediumSizeValue: Float = 9f
        private var typographyBodyMediumLineHeightValue: Float = 9f
        private var typographyBodyMediumLetterSpacingValue: Float = 0f
        private var typographyBodySmallSizeValue: Float = 9f
        private var typographyBodySmallLineHeightValue: Float = 9f
        private var typographyBodySmallLetterSpacingValue: Float = 0f
        private var typographyLabelLargeSizeValue: Float = 9f
        private var typographyLabelLargeLineHeightValue: Float = 9f
        private var typographyLabelLargeLetterSpacingValue: Float = 0f
        private var typographyLabelMediumSizeValue: Float = 9f
        private var typographyLabelMediumLineHeightValue: Float = 9f
        private var typographyLabelMediumLetterSpacingValue: Float = 0f
        private var typographyLabelSmallSizeValue: Float = 9f
        private var typographyLabelSmallLineHeightValue: Float = 9f
        private var typographyLabelSmallLetterSpacingValue: Float = 0f
        private var fontFamilyValue: String = FontStyle.DEFAULT_NAME
        private var cornerRadiusXSmallValue: Float = 0f
        private var cornerRadiusSmallValue: Float = 0f
        private var cornerRadiusMediumValue: Float = 0f
        private var cornerRadiusLargeValue: Float = 0f
        private var cornerRadiusExtraLargeValue: Float = 0f
        private var cornerRadiusFullValue: Float = 0f
        private var borderWidthValue: Float = 1f
        private var sliderTrackHeightValue: Float = 4f
        private var sliderThumbSizeValue: Float = 12f
        private var stateLayerSizeValue: Float = 20f
        private var textPaddingXValue: Float = 6f
        private var slotHoverColorValue: Int = 0x80FFFFFF.toInt()
        private var slotPlaceholderInsetValue: Float = 1f
        private var slotRejectedShakeAmplitudeValue: Float = 2f
        private var slotRejectedShakeFrequencyValue: Float = (PI * 6.0).toFloat()
        private var slotFailedMaxAlphaValue: Float = 0.5f
        private var slotFailedColorValue: Int = 0xFFFF3030.toInt()
        private var slotSuccessMaxAlphaValue: Float = 0.4f
        private var slotSuccessColorValue: Int = 0xFF55C266.toInt()
        private var slotTimeoutMaxAlphaValue: Float = 0.4f
        private var slotTimeoutColorValue: Int = 0xFF808080.toInt()
        private var slotQuickCraftPreviewAlphaValue: Float = 0.7f
        private var slotRejectedReplayAlphaValue: Float = 0.6f
        private var longPressThresholdMsValue: Long = 500L
        private var doubleClickThresholdMsValue: Long = 250L
        private var textPaddingYValue: Float = 0f
        private var textBaselineYValue: Float = 0f
        private var buttonCornerRadiusValue: Float = 0f
        private var buttonIconSpacingValue: Float = 2f
        private var buttonElevationDefaultValue: Float = 0f
        private var buttonElevationHoverValue: Float = 0f
        private var buttonElevationPressedValue: Float = 0f
        private var buttonTextTransformValue: String = "none"
        private var slotBorderColorValue: Int = 0x00000000
        private var slotBorderWidthValue: Float = 0f
        private var slotCornerRadiusValue: Float = 0f
        private var slotSelectedOutlineColorValue: Int = 0xFFFFD54F.toInt()
        private var slotSelectedOutlineWidthValue: Float = 1f
        private var slotPressedOpacityValue: Float = 0.10f
        private var slotFocusOpacityValue: Float = 0.10f
        private var slotDisabledOpacityValue: Float = 0.38f
        private var slotHoverOpacityValue: Float = 0.08f
        private var slotFocusOverlayColorValue: Int = 0x80FFFFFF.toInt()
        private var slotDisabledOverlayColorValue: Int = 0x80000000.toInt()
        private var slotBackgroundOuterColorValue: Int = 0xFF8B8B8B.toInt()
        private var slotBackgroundInnerColorValue: Int = 0xFF373737.toInt()
        private var progressBarTokensValue: ProgressBarTokens = ProgressBarTokens()
        private var itemStackBackgroundColorValue: Int = 0xFF8B8B8B.toInt()
        private var itemStackBackgroundInnerColorValue: Int = 0xFF373737.toInt()
        private var itemStackHoverColorValue: Int = 0x80FFFFFF.toInt()
        private var dropdownTokensValue: DropdownTokens = DropdownTokens()
        private var glowColorValue: Int = 0x00000000
        private var glowIntensityValue: Float = 0f
        private var glowSizeValue: Float = 0f
        private var frameStrokeColorValue: Int = 0x00000000
        private var frameStrokeWidthValue: Float = 0f
        private var scanlineColorValue: Int = 0x00000000
        private var scanlineDensityValue: Float = 0f
        private var animDurationEnterMsValue: Long = 0L
        private var animDurationExitMsValue: Long = 0L
        private var animDurationStaggerMsValue: Long = 0L
        private var tooltipDelayMsValue: Long = 500L
        private var tooltipBackgroundColorValue: Int = 0xE6000000.toInt()
        private var tooltipTextColorValue: Int = 0xFFFFFFFF.toInt()
        private var tooltipCornerRadiusValue: Float = 2f
        private var tooltipPaddingXValue: Float = 4f
        private var tooltipPaddingYValue: Float = 2f
        private var tooltipLineSpacingValue: Float = 1.2f
        private var defaultToastStyleValue: ToastStyle = ToastStyle.builder().build()
        private val toastStylesValue: LinkedHashMap<String, ToastStyle> = LinkedHashMap()

        fun primary(value: Int): Builder = apply { primaryValue = value }
        fun onPrimary(value: Int): Builder = apply { onPrimaryValue = value }
        fun primaryContainer(value: Int): Builder = apply { primaryContainerValue = value }
        fun onPrimaryContainer(value: Int): Builder = apply { onPrimaryContainerValue = value }
        fun inversePrimary(value: Int): Builder = apply { inversePrimaryValue = value }
        fun secondary(value: Int): Builder = apply { secondaryValue = value }
        fun onSecondary(value: Int): Builder = apply { onSecondaryValue = value }
        fun secondaryContainer(value: Int): Builder = apply { secondaryContainerValue = value }
        fun onSecondaryContainer(value: Int): Builder = apply { onSecondaryContainerValue = value }
        fun tertiary(value: Int): Builder = apply { tertiaryValue = value }
        fun onTertiary(value: Int): Builder = apply { onTertiaryValue = value }
        fun tertiaryContainer(value: Int): Builder = apply { tertiaryContainerValue = value }
        fun onTertiaryContainer(value: Int): Builder = apply { onTertiaryContainerValue = value }
        fun error(value: Int): Builder = apply { errorValue = value }
        fun onError(value: Int): Builder = apply { onErrorValue = value }
        fun errorContainer(value: Int): Builder = apply { errorContainerValue = value }
        fun onErrorContainer(value: Int): Builder = apply { onErrorContainerValue = value }
        fun successColor(value: Int): Builder = apply { successColorValue = value }
        fun onSuccess(value: Int): Builder = apply { onSuccessValue = value }
        fun warningColor(value: Int): Builder = apply { warningColorValue = value }
        fun onWarning(value: Int): Builder = apply { onWarningValue = value }
        fun infoColor(value: Int): Builder = apply { infoColorValue = value }
        fun onInfo(value: Int): Builder = apply { onInfoValue = value }
        fun background(value: Int): Builder = apply { backgroundValue = value }
        fun onBackground(value: Int): Builder = apply { onBackgroundValue = value }
        fun surface(value: Int): Builder = apply { surfaceValue = value }
        fun onSurface(value: Int): Builder = apply { onSurfaceValue = value }
        fun surfaceVariant(value: Int): Builder = apply { surfaceVariantValue = value }
        fun onSurfaceVariant(value: Int): Builder = apply { onSurfaceVariantValue = value }
        fun surfaceContainerLowest(value: Int): Builder = apply { surfaceContainerLowestValue = value }
        fun surfaceContainerLow(value: Int): Builder = apply { surfaceContainerLowValue = value }
        fun surfaceContainer(value: Int): Builder = apply { surfaceContainerValue = value }
        fun surfaceContainerHigh(value: Int): Builder = apply { surfaceContainerHighValue = value }
        fun surfaceContainerHighest(value: Int): Builder = apply { surfaceContainerHighestValue = value }
        fun surfaceTint(value: Int): Builder = apply { surfaceTintValue = value }
        fun inverseSurface(value: Int): Builder = apply { inverseSurfaceValue = value }
        fun inverseOnSurface(value: Int): Builder = apply { inverseOnSurfaceValue = value }
        fun outline(value: Int): Builder = apply { outlineValue = value }
        fun outlineVariant(value: Int): Builder = apply { outlineVariantValue = value }
        fun shadow(value: Int): Builder = apply { shadowValue = value }
        fun scrim(value: Int): Builder = apply { scrimValue = value }
        fun stateLayerHoverOpacity(value: Float): Builder = apply { stateLayerHoverOpacityValue = value }
        fun stateLayerFocusOpacity(value: Float): Builder = apply { stateLayerFocusOpacityValue = value }
        fun stateLayerPressedOpacity(value: Float): Builder = apply { stateLayerPressedOpacityValue = value }
        fun stateLayerDraggedOpacity(value: Float): Builder = apply { stateLayerDraggedOpacityValue = value }
        fun disabledOpacity(value: Float): Builder = apply { disabledOpacityValue = value }
        fun disabledContentOpacity(value: Float): Builder = apply { disabledContentOpacityValue = value }
        fun typographyDisplayLargeSize(value: Float): Builder = apply { typographyDisplayLargeSizeValue = value }
        fun typographyDisplayLargeLineHeight(value: Float): Builder = apply {
            typographyDisplayLargeLineHeightValue =
                value
        }
        fun typographyDisplayLargeLetterSpacing(value: Float): Builder = apply {
            typographyDisplayLargeLetterSpacingValue =
                value
        }
        fun typographyDisplayMediumSize(value: Float): Builder = apply { typographyDisplayMediumSizeValue = value }
        fun typographyDisplayMediumLineHeight(value: Float): Builder = apply {
            typographyDisplayMediumLineHeightValue =
                value
        }
        fun typographyDisplayMediumLetterSpacing(value: Float): Builder = apply {
            typographyDisplayMediumLetterSpacingValue =
                value
        }
        fun typographyDisplaySmallSize(value: Float): Builder = apply { typographyDisplaySmallSizeValue = value }
        fun typographyDisplaySmallLineHeight(value: Float): Builder = apply {
            typographyDisplaySmallLineHeightValue =
                value
        }
        fun typographyDisplaySmallLetterSpacing(value: Float): Builder = apply {
            typographyDisplaySmallLetterSpacingValue =
                value
        }
        fun typographyHeadlineLargeSize(value: Float): Builder = apply { typographyHeadlineLargeSizeValue = value }
        fun typographyHeadlineLargeLineHeight(value: Float): Builder = apply {
            typographyHeadlineLargeLineHeightValue =
                value
        }
        fun typographyHeadlineLargeLetterSpacing(value: Float): Builder = apply {
            typographyHeadlineLargeLetterSpacingValue =
                value
        }
        fun typographyHeadlineMediumSize(value: Float): Builder = apply { typographyHeadlineMediumSizeValue = value }
        fun typographyHeadlineMediumLineHeight(value: Float): Builder = apply {
            typographyHeadlineMediumLineHeightValue =
                value
        }
        fun typographyHeadlineMediumLetterSpacing(value: Float): Builder = apply {
            typographyHeadlineMediumLetterSpacingValue =
                value
        }
        fun typographyHeadlineSmallSize(value: Float): Builder = apply { typographyHeadlineSmallSizeValue = value }
        fun typographyHeadlineSmallLineHeight(value: Float): Builder = apply {
            typographyHeadlineSmallLineHeightValue =
                value
        }
        fun typographyHeadlineSmallLetterSpacing(value: Float): Builder = apply {
            typographyHeadlineSmallLetterSpacingValue =
                value
        }
        fun typographyTitleLargeSize(value: Float): Builder = apply { typographyTitleLargeSizeValue = value }
        fun typographyTitleLargeLineHeight(value: Float): Builder =
            apply { typographyTitleLargeLineHeightValue = value }
        fun typographyTitleLargeLetterSpacing(value: Float): Builder = apply {
            typographyTitleLargeLetterSpacingValue =
                value
        }
        fun typographyTitleMediumSize(value: Float): Builder = apply { typographyTitleMediumSizeValue = value }
        fun typographyTitleMediumLineHeight(value: Float): Builder = apply {
            typographyTitleMediumLineHeightValue =
                value
        }
        fun typographyTitleMediumLetterSpacing(value: Float): Builder = apply {
            typographyTitleMediumLetterSpacingValue =
                value
        }
        fun typographyTitleSmallSize(value: Float): Builder = apply { typographyTitleSmallSizeValue = value }
        fun typographyTitleSmallLineHeight(value: Float): Builder =
            apply { typographyTitleSmallLineHeightValue = value }
        fun typographyTitleSmallLetterSpacing(value: Float): Builder = apply {
            typographyTitleSmallLetterSpacingValue =
                value
        }
        fun typographyBodyLargeSize(value: Float): Builder = apply { typographyBodyLargeSizeValue = value }
        fun typographyBodyLargeLineHeight(value: Float): Builder = apply { typographyBodyLargeLineHeightValue = value }
        fun typographyBodyLargeLetterSpacing(value: Float): Builder = apply {
            typographyBodyLargeLetterSpacingValue =
                value
        }
        fun typographyBodyMediumSize(value: Float): Builder = apply { typographyBodyMediumSizeValue = value }
        fun typographyBodyMediumLineHeight(value: Float): Builder =
            apply { typographyBodyMediumLineHeightValue = value }
        fun typographyBodyMediumLetterSpacing(value: Float): Builder = apply {
            typographyBodyMediumLetterSpacingValue =
                value
        }
        fun typographyBodySmallSize(value: Float): Builder = apply { typographyBodySmallSizeValue = value }
        fun typographyBodySmallLineHeight(value: Float): Builder = apply { typographyBodySmallLineHeightValue = value }
        fun typographyBodySmallLetterSpacing(value: Float): Builder = apply {
            typographyBodySmallLetterSpacingValue =
                value
        }
        fun typographyLabelLargeSize(value: Float): Builder = apply { typographyLabelLargeSizeValue = value }
        fun typographyLabelLargeLineHeight(value: Float): Builder =
            apply { typographyLabelLargeLineHeightValue = value }
        fun typographyLabelLargeLetterSpacing(value: Float): Builder = apply {
            typographyLabelLargeLetterSpacingValue =
                value
        }
        fun typographyLabelMediumSize(value: Float): Builder = apply { typographyLabelMediumSizeValue = value }
        fun typographyLabelMediumLineHeight(value: Float): Builder = apply {
            typographyLabelMediumLineHeightValue =
                value
        }
        fun typographyLabelMediumLetterSpacing(value: Float): Builder = apply {
            typographyLabelMediumLetterSpacingValue =
                value
        }
        fun typographyLabelSmallSize(value: Float): Builder = apply { typographyLabelSmallSizeValue = value }
        fun typographyLabelSmallLineHeight(value: Float): Builder =
            apply { typographyLabelSmallLineHeightValue = value }
        fun typographyLabelSmallLetterSpacing(value: Float): Builder = apply {
            typographyLabelSmallLetterSpacingValue =
                value
        }
        fun fontFamily(value: String): Builder = apply { fontFamilyValue = value }
        fun cornerRadiusXSmall(value: Float): Builder = apply { cornerRadiusXSmallValue = value }
        fun cornerRadiusSmall(value: Float): Builder = apply { cornerRadiusSmallValue = value }
        fun cornerRadiusMedium(value: Float): Builder = apply { cornerRadiusMediumValue = value }
        fun cornerRadiusLarge(value: Float): Builder = apply { cornerRadiusLargeValue = value }
        fun cornerRadiusExtraLarge(value: Float): Builder = apply { cornerRadiusExtraLargeValue = value }
        fun cornerRadiusFull(value: Float): Builder = apply { cornerRadiusFullValue = value }
        fun borderWidth(value: Float): Builder = apply { borderWidthValue = value }
        fun sliderTrackHeight(value: Float): Builder = apply { sliderTrackHeightValue = value }
        fun sliderThumbSize(value: Float): Builder = apply { sliderThumbSizeValue = value }
        fun stateLayerSize(value: Float): Builder = apply { stateLayerSizeValue = value }
        fun textPaddingX(value: Float): Builder = apply { textPaddingXValue = value }
        fun slotHoverColor(value: Int): Builder = apply { slotHoverColorValue = value }
        fun slotPlaceholderInset(value: Float): Builder = apply { slotPlaceholderInsetValue = value }
        fun slotRejectedShakeAmplitude(value: Float): Builder = apply { slotRejectedShakeAmplitudeValue = value }
        fun slotRejectedShakeFrequency(value: Float): Builder = apply { slotRejectedShakeFrequencyValue = value }
        fun slotFailedMaxAlpha(value: Float): Builder = apply { slotFailedMaxAlphaValue = value }
        fun slotFailedColor(value: Int): Builder = apply { slotFailedColorValue = value }
        fun slotSuccessMaxAlpha(value: Float): Builder = apply { slotSuccessMaxAlphaValue = value }
        fun slotSuccessColor(value: Int): Builder = apply { slotSuccessColorValue = value }
        fun slotTimeoutMaxAlpha(value: Float): Builder = apply { slotTimeoutMaxAlphaValue = value }
        fun slotTimeoutColor(value: Int): Builder = apply { slotTimeoutColorValue = value }
        fun slotQuickCraftPreviewAlpha(value: Float): Builder = apply { slotQuickCraftPreviewAlphaValue = value }
        fun slotRejectedReplayAlpha(value: Float): Builder = apply { slotRejectedReplayAlphaValue = value }
        fun longPressThresholdMs(value: Long): Builder = apply { longPressThresholdMsValue = value }
        fun doubleClickThresholdMs(value: Long): Builder = apply { doubleClickThresholdMsValue = value }
        fun textPaddingY(value: Float): Builder = apply { textPaddingYValue = value }
        fun textBaselineY(value: Float): Builder = apply { textBaselineYValue = value }
        fun buttonCornerRadius(value: Float): Builder = apply { buttonCornerRadiusValue = value }
        fun buttonIconSpacing(value: Float): Builder = apply { buttonIconSpacingValue = value }
        fun buttonElevationDefault(value: Float): Builder = apply { buttonElevationDefaultValue = value }
        fun buttonElevationHover(value: Float): Builder = apply { buttonElevationHoverValue = value }
        fun buttonElevationPressed(value: Float): Builder = apply { buttonElevationPressedValue = value }
        fun buttonTextTransform(value: String): Builder = apply { buttonTextTransformValue = value }
        fun slotBorderColor(value: Int): Builder = apply { slotBorderColorValue = value }
        fun slotBorderWidth(value: Float): Builder = apply { slotBorderWidthValue = value }
        fun slotCornerRadius(value: Float): Builder = apply { slotCornerRadiusValue = value }
        fun slotSelectedOutlineColor(value: Int): Builder = apply { slotSelectedOutlineColorValue = value }
        fun slotSelectedOutlineWidth(value: Float): Builder = apply { slotSelectedOutlineWidthValue = value }
        fun slotPressedOpacity(value: Float): Builder = apply { slotPressedOpacityValue = value }
        fun slotFocusOpacity(value: Float): Builder = apply { slotFocusOpacityValue = value }
        fun slotDisabledOpacity(value: Float): Builder = apply { slotDisabledOpacityValue = value }
        fun slotHoverOpacity(value: Float): Builder = apply { slotHoverOpacityValue = value }
        fun slotFocusOverlayColor(value: Int): Builder = apply { slotFocusOverlayColorValue = value }
        fun slotDisabledOverlayColor(value: Int): Builder = apply { slotDisabledOverlayColorValue = value }
        fun slotBackgroundOuterColor(value: Int): Builder = apply { slotBackgroundOuterColorValue = value }
        fun slotBackgroundInnerColor(value: Int): Builder = apply { slotBackgroundInnerColorValue = value }
        fun progressBarNotchColor(value: Int): Builder = apply {
            progressBarTokensValue = progressBarTokensValue.copy(notchColor = value)
        }
        fun itemStackBackgroundColor(value: Int): Builder = apply { itemStackBackgroundColorValue = value }
        fun itemStackBackgroundInnerColor(value: Int): Builder = apply { itemStackBackgroundInnerColorValue = value }
        fun itemStackHoverColor(value: Int): Builder = apply { itemStackHoverColorValue = value }
        fun dropdownOptionHoverColor(value: Int): Builder = apply {
            dropdownTokensValue = dropdownTokensValue.copy(optionHoverColor = value)
        }
        fun glowColor(value: Int): Builder = apply { glowColorValue = value }
        fun glowIntensity(value: Float): Builder = apply { glowIntensityValue = value }
        fun glowSize(value: Float): Builder = apply { glowSizeValue = value }
        fun frameStrokeColor(value: Int): Builder = apply { frameStrokeColorValue = value }
        fun frameStrokeWidth(value: Float): Builder = apply { frameStrokeWidthValue = value }
        fun scanlineColor(value: Int): Builder = apply { scanlineColorValue = value }
        fun scanlineDensity(value: Float): Builder = apply { scanlineDensityValue = value }
        fun animDurationEnterMs(value: Long): Builder = apply { animDurationEnterMsValue = value }
        fun animDurationExitMs(value: Long): Builder = apply { animDurationExitMsValue = value }
        fun animDurationStaggerMs(value: Long): Builder = apply { animDurationStaggerMsValue = value }
        fun tooltipDelayMs(value: Long): Builder = apply { tooltipDelayMsValue = value }
        fun tooltipBackgroundColor(value: Int): Builder = apply { tooltipBackgroundColorValue = value }
        fun tooltipTextColor(value: Int): Builder = apply { tooltipTextColorValue = value }
        fun tooltipCornerRadius(value: Float): Builder = apply { tooltipCornerRadiusValue = value }
        fun tooltipPaddingX(value: Float): Builder = apply { tooltipPaddingXValue = value }
        fun tooltipPaddingY(value: Float): Builder = apply { tooltipPaddingYValue = value }
        fun tooltipLineSpacing(value: Float): Builder = apply { tooltipLineSpacingValue = value }
        fun defaultToastStyle(value: ToastStyle): Builder = apply { defaultToastStyleValue = value }

        /** Lombok `@Singular` 单项写入：put 一个命名样式。 */
        fun toastStyle(key: String, value: ToastStyle): Builder = apply { toastStylesValue[key] = value }

        /** Lombok `@Singular` 复数写入：putAll 一批命名样式。 */
        fun toastStyles(map: Map<String, ToastStyle>): Builder = apply { toastStylesValue.putAll(map) }

        /** Lombok `@Singular` 清空：清空当前命名样式表。 */
        fun clearToastStyles(): Builder = apply { toastStylesValue.clear() }

        fun build(): ControlTheme = ControlTheme(
            DesignTokens(
                ColorTokens(
                    primaryValue, onPrimaryValue, primaryContainerValue, onPrimaryContainerValue, inversePrimaryValue,
                    secondaryValue, onSecondaryValue, secondaryContainerValue, onSecondaryContainerValue,
                    tertiaryValue, onTertiaryValue, tertiaryContainerValue, onTertiaryContainerValue,
                    errorValue, onErrorValue, errorContainerValue, onErrorContainerValue,
                    successColorValue, onSuccessValue, warningColorValue, onWarningValue, infoColorValue, onInfoValue,
                    backgroundValue, onBackgroundValue, surfaceValue, onSurfaceValue, surfaceVariantValue,
                    onSurfaceVariantValue, surfaceContainerLowestValue, surfaceContainerLowValue, surfaceContainerValue,
                    surfaceContainerHighValue, surfaceContainerHighestValue, surfaceTintValue, inverseSurfaceValue,
                    inverseOnSurfaceValue, outlineValue, outlineVariantValue, shadowValue, scrimValue,
                ),
                StateLayerTokens(
                    stateLayerHoverOpacityValue,
                    stateLayerFocusOpacityValue,
                    stateLayerPressedOpacityValue,
                    stateLayerDraggedOpacityValue,
                    disabledOpacityValue,
                    disabledContentOpacityValue,
                ),
                TypographyTokens(
                    typographyDisplayLargeSizeValue, typographyDisplayLargeLineHeightValue,
                    typographyDisplayLargeLetterSpacingValue, typographyDisplayMediumSizeValue,
                    typographyDisplayMediumLineHeightValue, typographyDisplayMediumLetterSpacingValue,
                    typographyDisplaySmallSizeValue, typographyDisplaySmallLineHeightValue,
                    typographyDisplaySmallLetterSpacingValue, typographyHeadlineLargeSizeValue,
                    typographyHeadlineLargeLineHeightValue, typographyHeadlineLargeLetterSpacingValue,
                    typographyHeadlineMediumSizeValue, typographyHeadlineMediumLineHeightValue,
                    typographyHeadlineMediumLetterSpacingValue, typographyHeadlineSmallSizeValue,
                    typographyHeadlineSmallLineHeightValue, typographyHeadlineSmallLetterSpacingValue,
                    typographyTitleLargeSizeValue, typographyTitleLargeLineHeightValue,
                    typographyTitleLargeLetterSpacingValue, typographyTitleMediumSizeValue,
                    typographyTitleMediumLineHeightValue, typographyTitleMediumLetterSpacingValue,
                    typographyTitleSmallSizeValue, typographyTitleSmallLineHeightValue,
                    typographyTitleSmallLetterSpacingValue, typographyBodyLargeSizeValue,
                    typographyBodyLargeLineHeightValue, typographyBodyLargeLetterSpacingValue,
                    typographyBodyMediumSizeValue, typographyBodyMediumLineHeightValue,
                    typographyBodyMediumLetterSpacingValue, typographyBodySmallSizeValue,
                    typographyBodySmallLineHeightValue, typographyBodySmallLetterSpacingValue,
                    typographyLabelLargeSizeValue, typographyLabelLargeLineHeightValue,
                    typographyLabelLargeLetterSpacingValue, typographyLabelMediumSizeValue,
                    typographyLabelMediumLineHeightValue, typographyLabelMediumLetterSpacingValue,
                    typographyLabelSmallSizeValue, typographyLabelSmallLineHeightValue,
                    typographyLabelSmallLetterSpacingValue, fontFamilyValue,
                ),
                ShapeTokens(
                    cornerRadiusXSmallValue,
                    cornerRadiusSmallValue,
                    cornerRadiusMediumValue,
                    cornerRadiusLargeValue,
                    cornerRadiusExtraLargeValue,
                    cornerRadiusFullValue,
                ),
                SharedGeometryTokens(
                    borderWidthValue,
                    sliderTrackHeightValue,
                    sliderThumbSizeValue,
                    stateLayerSizeValue,
                    textPaddingXValue,
                ),
                DecorationTokens(
                    glowColorValue, glowIntensityValue, glowSizeValue, frameStrokeColorValue, frameStrokeWidthValue,
                    scanlineColorValue, scanlineDensityValue, animDurationEnterMsValue, animDurationExitMsValue,
                    animDurationStaggerMsValue,
                ),
            ),
            SlotTokens(
                slotHoverColorValue,
                slotPlaceholderInsetValue,
                slotRejectedShakeAmplitudeValue,
                slotRejectedShakeFrequencyValue,
                slotFailedMaxAlphaValue,
                slotFailedColorValue,
                slotSuccessMaxAlphaValue,
                slotSuccessColorValue,
                slotTimeoutMaxAlphaValue,
                slotTimeoutColorValue,
                slotQuickCraftPreviewAlphaValue,
                slotRejectedReplayAlphaValue,
                slotBorderColorValue,
                slotBorderWidthValue,
                slotCornerRadiusValue,
                slotSelectedOutlineColorValue,
                slotSelectedOutlineWidthValue,
                slotPressedOpacityValue,
                slotFocusOpacityValue,
                slotDisabledOpacityValue,
                slotHoverOpacityValue,
                slotFocusOverlayColorValue,
                slotDisabledOverlayColorValue,
                slotBackgroundOuterColorValue,
                slotBackgroundInnerColorValue,
            ),
            longPressThresholdMsValue, doubleClickThresholdMsValue,
            ButtonTokens(
                textPaddingYValue,
                textBaselineYValue,
                buttonCornerRadiusValue,
                buttonIconSpacingValue,
                buttonElevationDefaultValue,
                buttonElevationHoverValue,
                buttonElevationPressedValue,
                buttonTextTransformValue,
            ),
            progressBarTokensValue,
            ItemStackTokens(
                itemStackBackgroundColorValue,
                itemStackBackgroundInnerColorValue,
                itemStackHoverColorValue,
            ),
            dropdownTokensValue,
            TooltipTokens(
                tooltipDelayMsValue,
                tooltipBackgroundColorValue,
                tooltipTextColorValue,
                tooltipCornerRadiusValue,
                tooltipPaddingXValue,
                tooltipPaddingYValue,
                tooltipLineSpacingValue,
            ),
            ToastTokens(
                defaultToastStyleValue,
                // 只读拷贝（保插入序）：与迁移前 Collections.unmodifiableMap(LinkedHashMap(...)) 语义等价
                toastStylesValue.toMap(),
            ),
        )
    }

    companion object {
        /** 构建器入口，复刻 Lombok 生成的 `ControlTheme.builder()`。 */
        @JvmStatic
        fun builder(): Builder = Builder()

        /**
         * 默认兜底主题：未声明 `theme {}` 或主题资源加载失败时使用。
         * 数值与现行渲染器硬编码常量一一对应，保持视觉零回归。
         */
        @JvmStatic
        fun defaultFallback(): ControlTheme = LEGACY_FALLBACK

        /**
         * 在两套主题之间按进度 t（0..1）插值，供 `local.setTheme` 平滑过渡。
         *
         * 仅对核心调色板与语义状态色逐通道插值；其余字段直接取 to（snap）。
         * 通过 toBuilder 自 to 复制基底后仅覆写需插值的颜色字段。
         */
        @JvmStatic
        fun lerp(from: ControlTheme, to: ControlTheme, t: Double): ControlTheme {
            if (t <= 0.0) return from
            if (t >= 1.0) return to
            return to.toBuilder()
                .primary(ColorFunctions.lerpArgb(from.primary, to.primary, t))
                .onPrimary(ColorFunctions.lerpArgb(from.onPrimary, to.onPrimary, t))
                .primaryContainer(ColorFunctions.lerpArgb(from.primaryContainer, to.primaryContainer, t))
                .onPrimaryContainer(ColorFunctions.lerpArgb(from.onPrimaryContainer, to.onPrimaryContainer, t))
                .inversePrimary(ColorFunctions.lerpArgb(from.inversePrimary, to.inversePrimary, t))
                .secondary(ColorFunctions.lerpArgb(from.secondary, to.secondary, t))
                .onSecondary(ColorFunctions.lerpArgb(from.onSecondary, to.onSecondary, t))
                .secondaryContainer(ColorFunctions.lerpArgb(from.secondaryContainer, to.secondaryContainer, t))
                .onSecondaryContainer(ColorFunctions.lerpArgb(from.onSecondaryContainer, to.onSecondaryContainer, t))
                .tertiary(ColorFunctions.lerpArgb(from.tertiary, to.tertiary, t))
                .onTertiary(ColorFunctions.lerpArgb(from.onTertiary, to.onTertiary, t))
                .tertiaryContainer(ColorFunctions.lerpArgb(from.tertiaryContainer, to.tertiaryContainer, t))
                .onTertiaryContainer(ColorFunctions.lerpArgb(from.onTertiaryContainer, to.onTertiaryContainer, t))
                .error(ColorFunctions.lerpArgb(from.error, to.error, t))
                .onError(ColorFunctions.lerpArgb(from.onError, to.onError, t))
                .errorContainer(ColorFunctions.lerpArgb(from.errorContainer, to.errorContainer, t))
                .onErrorContainer(ColorFunctions.lerpArgb(from.onErrorContainer, to.onErrorContainer, t))
                .successColor(ColorFunctions.lerpArgb(from.successColor, to.successColor, t))
                .onSuccess(ColorFunctions.lerpArgb(from.onSuccess, to.onSuccess, t))
                .warningColor(ColorFunctions.lerpArgb(from.warningColor, to.warningColor, t))
                .onWarning(ColorFunctions.lerpArgb(from.onWarning, to.onWarning, t))
                .infoColor(ColorFunctions.lerpArgb(from.infoColor, to.infoColor, t))
                .onInfo(ColorFunctions.lerpArgb(from.onInfo, to.onInfo, t))
                .background(ColorFunctions.lerpArgb(from.background, to.background, t))
                .onBackground(ColorFunctions.lerpArgb(from.onBackground, to.onBackground, t))
                .surface(ColorFunctions.lerpArgb(from.surface, to.surface, t))
                .onSurface(ColorFunctions.lerpArgb(from.onSurface, to.onSurface, t))
                .surfaceVariant(ColorFunctions.lerpArgb(from.surfaceVariant, to.surfaceVariant, t))
                .onSurfaceVariant(ColorFunctions.lerpArgb(from.onSurfaceVariant, to.onSurfaceVariant, t))
                .surfaceContainerLowest(
                    ColorFunctions.lerpArgb(from.surfaceContainerLowest, to.surfaceContainerLowest, t),
                )
                .surfaceContainerLow(ColorFunctions.lerpArgb(from.surfaceContainerLow, to.surfaceContainerLow, t))
                .surfaceContainer(ColorFunctions.lerpArgb(from.surfaceContainer, to.surfaceContainer, t))
                .surfaceContainerHigh(ColorFunctions.lerpArgb(from.surfaceContainerHigh, to.surfaceContainerHigh, t))
                .surfaceContainerHighest(
                    ColorFunctions.lerpArgb(from.surfaceContainerHighest, to.surfaceContainerHighest, t),
                )
                .surfaceTint(ColorFunctions.lerpArgb(from.surfaceTint, to.surfaceTint, t))
                .inverseSurface(ColorFunctions.lerpArgb(from.inverseSurface, to.inverseSurface, t))
                .inverseOnSurface(ColorFunctions.lerpArgb(from.inverseOnSurface, to.inverseOnSurface, t))
                .outline(ColorFunctions.lerpArgb(from.outline, to.outline, t))
                .outlineVariant(ColorFunctions.lerpArgb(from.outlineVariant, to.outlineVariant, t))
                .shadow(ColorFunctions.lerpArgb(from.shadow, to.shadow, t))
                .scrim(ColorFunctions.lerpArgb(from.scrim, to.scrim, t))
                .build()
        }

        /** 兼容旧调用名，保留一个版本；新代码请使用 [defaultFallback]。 */
        @JvmStatic
        @Deprecated("使用 defaultFallback()", ReplaceWith("defaultFallback()"))
        fun legacyFallback(): ControlTheme = defaultFallback()

        /**
         * 把 [ThemeLookup] 中的扁平 token 表解码为本类实例。
         * token 缺失时回退到 fallback 对应字段；fallback 为 null 时退化到 [defaultFallback]。
         */
        @JvmStatic
        fun fromLookup(lookup: ThemeLookup?, fallback: ControlTheme?): ControlTheme {
            val fb = fallback ?: defaultFallback()
            if (lookup == null) {
                return fb
            }
            return decode(lookup, fb)
        }

        /** 把所有 token 从 lookup 读出来；缺失则取 fb 字段。 */
        private fun decode(lookup: ThemeLookup, fb: ControlTheme): ControlTheme {
            val b = builder()
                .primary(lookup.getColor("primary", fb.primary))
                .onPrimary(lookup.getColor("onPrimary", fb.onPrimary))
                .primaryContainer(lookup.getColor("primaryContainer", fb.primaryContainer))
                .onPrimaryContainer(lookup.getColor("onPrimaryContainer", fb.onPrimaryContainer))
                .inversePrimary(lookup.getColor("inversePrimary", fb.inversePrimary))
                .secondary(lookup.getColor("secondary", fb.secondary))
                .onSecondary(lookup.getColor("onSecondary", fb.onSecondary))
                .secondaryContainer(lookup.getColor("secondaryContainer", fb.secondaryContainer))
                .onSecondaryContainer(lookup.getColor("onSecondaryContainer", fb.onSecondaryContainer))
                .tertiary(lookup.getColor("tertiary", fb.tertiary))
                .onTertiary(lookup.getColor("onTertiary", fb.onTertiary))
                .tertiaryContainer(lookup.getColor("tertiaryContainer", fb.tertiaryContainer))
                .onTertiaryContainer(lookup.getColor("onTertiaryContainer", fb.onTertiaryContainer))
                .error(lookup.getColor("error", fb.error))
                .onError(lookup.getColor("onError", fb.onError))
                .errorContainer(lookup.getColor("errorContainer", fb.errorContainer))
                .onErrorContainer(lookup.getColor("onErrorContainer", fb.onErrorContainer))
                .successColor(lookup.getColor("successColor", fb.successColor))
                .onSuccess(lookup.getColor("onSuccess", fb.onSuccess))
                .warningColor(lookup.getColor("warningColor", fb.warningColor))
                .onWarning(lookup.getColor("onWarning", fb.onWarning))
                .infoColor(lookup.getColor("infoColor", fb.infoColor))
                .onInfo(lookup.getColor("onInfo", fb.onInfo))
                .background(lookup.getColor("background", fb.background))
                .onBackground(lookup.getColor("onBackground", fb.onBackground))
                .surface(lookup.getColor("surface", fb.surface))
                .onSurface(lookup.getColor("onSurface", fb.onSurface))
                .surfaceVariant(lookup.getColor("surfaceVariant", fb.surfaceVariant))
                .onSurfaceVariant(lookup.getColor("onSurfaceVariant", fb.onSurfaceVariant))
                .surfaceContainerLowest(lookup.getColor("surfaceContainerLowest", fb.surfaceContainerLowest))
                .surfaceContainerLow(lookup.getColor("surfaceContainerLow", fb.surfaceContainerLow))
                .surfaceContainer(lookup.getColor("surfaceContainer", fb.surfaceContainer))
                .surfaceContainerHigh(lookup.getColor("surfaceContainerHigh", fb.surfaceContainerHigh))
                .surfaceContainerHighest(lookup.getColor("surfaceContainerHighest", fb.surfaceContainerHighest))
                .surfaceTint(lookup.getColor("surfaceTint", fb.surfaceTint))
                .inverseSurface(lookup.getColor("inverseSurface", fb.inverseSurface))
                .inverseOnSurface(lookup.getColor("inverseOnSurface", fb.inverseOnSurface))
                .outline(lookup.getColor("outline", fb.outline))
                .outlineVariant(lookup.getColor("outlineVariant", fb.outlineVariant))
                .shadow(lookup.getColor("shadow", fb.shadow))
                .scrim(lookup.getColor("scrim", fb.scrim))
                .stateLayerHoverOpacity(
                    lookup.getNumber("stateLayerHoverOpacity", fb.stateLayerHoverOpacity.toDouble()).toFloat(),
                )
                .stateLayerFocusOpacity(
                    lookup.getNumber("stateLayerFocusOpacity", fb.stateLayerFocusOpacity.toDouble()).toFloat(),
                )
                .stateLayerPressedOpacity(
                    lookup.getNumber("stateLayerPressedOpacity", fb.stateLayerPressedOpacity.toDouble()).toFloat(),
                )
                .stateLayerDraggedOpacity(
                    lookup.getNumber("stateLayerDraggedOpacity", fb.stateLayerDraggedOpacity.toDouble()).toFloat(),
                )
                .disabledOpacity(lookup.getNumber("disabledOpacity", fb.disabledOpacity.toDouble()).toFloat())
                .disabledContentOpacity(
                    lookup.getNumber("disabledContentOpacity", fb.disabledContentOpacity.toDouble()).toFloat(),
                )
            decodeTypography(b, lookup, fb)
            decodeShape(b, lookup, fb)
            decodeSlot(b, lookup, fb)
            decodeButton(b, lookup, fb)
            decodeTooltip(b, lookup, fb)
            decodeControlOverrides(b, lookup, fb)
            decodeDecorationEffects(b, lookup, fb)
            decodeToast(b, lookup, fb)
            return b.build()
        }

        private fun decodeTypography(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .typographyDisplayLargeSize(
                    lookup.getNumber("typographyDisplayLargeSize", fb.typographyDisplayLargeSize.toDouble()).toFloat(),
                )
                .typographyDisplayLargeLineHeight(
                    lookup.getNumber(
                        "typographyDisplayLargeLineHeight",
                        fb.typographyDisplayLargeLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplayLargeLetterSpacing(
                    lookup.getNumber(
                        "typographyDisplayLargeLetterSpacing",
                        fb.typographyDisplayLargeLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplayMediumSize(
                    lookup.getNumber(
                        "typographyDisplayMediumSize",
                        fb.typographyDisplayMediumSize.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplayMediumLineHeight(
                    lookup.getNumber(
                        "typographyDisplayMediumLineHeight",
                        fb.typographyDisplayMediumLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplayMediumLetterSpacing(
                    lookup.getNumber(
                        "typographyDisplayMediumLetterSpacing",
                        fb.typographyDisplayMediumLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplaySmallSize(
                    lookup.getNumber("typographyDisplaySmallSize", fb.typographyDisplaySmallSize.toDouble()).toFloat(),
                )
                .typographyDisplaySmallLineHeight(
                    lookup.getNumber(
                        "typographyDisplaySmallLineHeight",
                        fb.typographyDisplaySmallLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyDisplaySmallLetterSpacing(
                    lookup.getNumber(
                        "typographyDisplaySmallLetterSpacing",
                        fb.typographyDisplaySmallLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineLargeSize(
                    lookup.getNumber(
                        "typographyHeadlineLargeSize",
                        fb.typographyHeadlineLargeSize.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineLargeLineHeight(
                    lookup.getNumber(
                        "typographyHeadlineLargeLineHeight",
                        fb.typographyHeadlineLargeLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineLargeLetterSpacing(
                    lookup.getNumber(
                        "typographyHeadlineLargeLetterSpacing",
                        fb.typographyHeadlineLargeLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineMediumSize(
                    lookup.getNumber(
                        "typographyHeadlineMediumSize",
                        fb.typographyHeadlineMediumSize.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineMediumLineHeight(
                    lookup.getNumber(
                        "typographyHeadlineMediumLineHeight",
                        fb.typographyHeadlineMediumLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineMediumLetterSpacing(
                    lookup.getNumber(
                        "typographyHeadlineMediumLetterSpacing",
                        fb.typographyHeadlineMediumLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineSmallSize(
                    lookup.getNumber(
                        "typographyHeadlineSmallSize",
                        fb.typographyHeadlineSmallSize.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineSmallLineHeight(
                    lookup.getNumber(
                        "typographyHeadlineSmallLineHeight",
                        fb.typographyHeadlineSmallLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyHeadlineSmallLetterSpacing(
                    lookup.getNumber(
                        "typographyHeadlineSmallLetterSpacing",
                        fb.typographyHeadlineSmallLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleLargeSize(
                    lookup.getNumber("typographyTitleLargeSize", fb.typographyTitleLargeSize.toDouble()).toFloat(),
                )
                .typographyTitleLargeLineHeight(
                    lookup.getNumber(
                        "typographyTitleLargeLineHeight",
                        fb.typographyTitleLargeLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleLargeLetterSpacing(
                    lookup.getNumber(
                        "typographyTitleLargeLetterSpacing",
                        fb.typographyTitleLargeLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleMediumSize(
                    lookup.getNumber("typographyTitleMediumSize", fb.typographyTitleMediumSize.toDouble()).toFloat(),
                )
                .typographyTitleMediumLineHeight(
                    lookup.getNumber(
                        "typographyTitleMediumLineHeight",
                        fb.typographyTitleMediumLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleMediumLetterSpacing(
                    lookup.getNumber(
                        "typographyTitleMediumLetterSpacing",
                        fb.typographyTitleMediumLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleSmallSize(
                    lookup.getNumber("typographyTitleSmallSize", fb.typographyTitleSmallSize.toDouble()).toFloat(),
                )
                .typographyTitleSmallLineHeight(
                    lookup.getNumber(
                        "typographyTitleSmallLineHeight",
                        fb.typographyTitleSmallLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyTitleSmallLetterSpacing(
                    lookup.getNumber(
                        "typographyTitleSmallLetterSpacing",
                        fb.typographyTitleSmallLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyBodyLargeSize(
                    lookup.getNumber("typographyBodyLargeSize", fb.typographyBodyLargeSize.toDouble()).toFloat(),
                )
                .typographyBodyLargeLineHeight(
                    lookup.getNumber(
                        "typographyBodyLargeLineHeight",
                        fb.typographyBodyLargeLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyBodyLargeLetterSpacing(
                    lookup.getNumber(
                        "typographyBodyLargeLetterSpacing",
                        fb.typographyBodyLargeLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyBodyMediumSize(
                    lookup.getNumber("typographyBodyMediumSize", fb.typographyBodyMediumSize.toDouble()).toFloat(),
                )
                .typographyBodyMediumLineHeight(
                    lookup.getNumber(
                        "typographyBodyMediumLineHeight",
                        fb.typographyBodyMediumLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyBodyMediumLetterSpacing(
                    lookup.getNumber(
                        "typographyBodyMediumLetterSpacing",
                        fb.typographyBodyMediumLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyBodySmallSize(
                    lookup.getNumber("typographyBodySmallSize", fb.typographyBodySmallSize.toDouble()).toFloat(),
                )
                .typographyBodySmallLineHeight(
                    lookup.getNumber(
                        "typographyBodySmallLineHeight",
                        fb.typographyBodySmallLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyBodySmallLetterSpacing(
                    lookup.getNumber(
                        "typographyBodySmallLetterSpacing",
                        fb.typographyBodySmallLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelLargeSize(
                    lookup.getNumber("typographyLabelLargeSize", fb.typographyLabelLargeSize.toDouble()).toFloat(),
                )
                .typographyLabelLargeLineHeight(
                    lookup.getNumber(
                        "typographyLabelLargeLineHeight",
                        fb.typographyLabelLargeLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelLargeLetterSpacing(
                    lookup.getNumber(
                        "typographyLabelLargeLetterSpacing",
                        fb.typographyLabelLargeLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelMediumSize(
                    lookup.getNumber("typographyLabelMediumSize", fb.typographyLabelMediumSize.toDouble()).toFloat(),
                )
                .typographyLabelMediumLineHeight(
                    lookup.getNumber(
                        "typographyLabelMediumLineHeight",
                        fb.typographyLabelMediumLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelMediumLetterSpacing(
                    lookup.getNumber(
                        "typographyLabelMediumLetterSpacing",
                        fb.typographyLabelMediumLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelSmallSize(
                    lookup.getNumber("typographyLabelSmallSize", fb.typographyLabelSmallSize.toDouble()).toFloat(),
                )
                .typographyLabelSmallLineHeight(
                    lookup.getNumber(
                        "typographyLabelSmallLineHeight",
                        fb.typographyLabelSmallLineHeight.toDouble(),
                    ).toFloat(),
                )
                .typographyLabelSmallLetterSpacing(
                    lookup.getNumber(
                        "typographyLabelSmallLetterSpacing",
                        fb.typographyLabelSmallLetterSpacing.toDouble(),
                    ).toFloat(),
                )
                .fontFamily(lookup.getString("fontFamily", fb.fontFamily) ?: fb.fontFamily)
        }

        private fun decodeShape(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .cornerRadiusXSmall(lookup.getNumber("cornerRadiusXSmall", fb.cornerRadiusXSmall.toDouble()).toFloat())
                .cornerRadiusSmall(lookup.getNumber("cornerRadiusSmall", fb.cornerRadiusSmall.toDouble()).toFloat())
                .cornerRadiusMedium(lookup.getNumber("cornerRadiusMedium", fb.cornerRadiusMedium.toDouble()).toFloat())
                .cornerRadiusLarge(lookup.getNumber("cornerRadiusLarge", fb.cornerRadiusLarge.toDouble()).toFloat())
                .cornerRadiusExtraLarge(
                    lookup.getNumber("cornerRadiusExtraLarge", fb.cornerRadiusExtraLarge.toDouble()).toFloat(),
                )
                .cornerRadiusFull(lookup.getNumber("cornerRadiusFull", fb.cornerRadiusFull.toDouble()).toFloat())
                .borderWidth(lookup.getNumber("borderWidth", fb.borderWidth.toDouble()).toFloat())
                .sliderTrackHeight(lookup.getNumber("sliderTrackHeight", fb.sliderTrackHeight.toDouble()).toFloat())
                .sliderThumbSize(lookup.getNumber("sliderThumbSize", fb.sliderThumbSize.toDouble()).toFloat())
                .stateLayerSize(lookup.getNumber("stateLayerSize", fb.stateLayerSize.toDouble()).toFloat())
                .textPaddingX(lookup.getNumber("textPaddingX", fb.textPaddingX.toDouble()).toFloat())
        }

        private fun decodeSlot(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .slotHoverColor(lookup.getColor("slotHoverColor", fb.slotHoverColor))
                .slotPlaceholderInset(
                    lookup.getNumber("slotPlaceholderInset", fb.slotPlaceholderInset.toDouble()).toFloat(),
                )
                .slotRejectedShakeAmplitude(
                    lookup.getNumber("slotRejectedShakeAmplitude", fb.slotRejectedShakeAmplitude.toDouble()).toFloat(),
                )
                .slotRejectedShakeFrequency(
                    lookup.getNumber("slotRejectedShakeFrequency", fb.slotRejectedShakeFrequency.toDouble()).toFloat(),
                )
                .slotFailedMaxAlpha(lookup.getNumber("slotFailedMaxAlpha", fb.slotFailedMaxAlpha.toDouble()).toFloat())
                .slotFailedColor(lookup.getColor("slotFailedColor", fb.slotFailedColor))
                .slotSuccessMaxAlpha(
                    lookup.getNumber("slotSuccessMaxAlpha", fb.slotSuccessMaxAlpha.toDouble()).toFloat(),
                )
                .slotSuccessColor(lookup.getColor("slotSuccessColor", fb.slotSuccessColor))
                .slotTimeoutMaxAlpha(
                    lookup.getNumber("slotTimeoutMaxAlpha", fb.slotTimeoutMaxAlpha.toDouble()).toFloat(),
                )
                .slotTimeoutColor(lookup.getColor("slotTimeoutColor", fb.slotTimeoutColor))
                .slotQuickCraftPreviewAlpha(
                    lookup.getNumber("slotQuickCraftPreviewAlpha", fb.slotQuickCraftPreviewAlpha.toDouble()).toFloat(),
                )
                .slotRejectedReplayAlpha(
                    lookup.getNumber("slotRejectedReplayAlpha", fb.slotRejectedReplayAlpha.toDouble()).toFloat(),
                )
                .slotBorderColor(lookup.getColor("slotBorderColor", fb.slotBorderColor))
                .slotBorderWidth(lookup.getNumber("slotBorderWidth", fb.slotBorderWidth.toDouble()).toFloat())
                .slotCornerRadius(lookup.getNumber("slotCornerRadius", fb.slotCornerRadius.toDouble()).toFloat())
                .slotSelectedOutlineColor(lookup.getColor("slotSelectedOutlineColor", fb.slotSelectedOutlineColor))
                .slotSelectedOutlineWidth(
                    lookup.getNumber("slotSelectedOutlineWidth", fb.slotSelectedOutlineWidth.toDouble()).toFloat(),
                )
                .slotPressedOpacity(lookup.getNumber("slotPressedOpacity", fb.slotPressedOpacity.toDouble()).toFloat())
                .slotFocusOpacity(lookup.getNumber("slotFocusOpacity", fb.slotFocusOpacity.toDouble()).toFloat())
                .slotDisabledOpacity(
                    lookup.getNumber("slotDisabledOpacity", fb.slotDisabledOpacity.toDouble()).toFloat(),
                )
                .slotHoverOpacity(lookup.getNumber("slotHoverOpacity", fb.slotHoverOpacity.toDouble()).toFloat())
                .slotFocusOverlayColor(lookup.getColor("slotFocusOverlayColor", fb.slotFocusOverlayColor))
                .slotDisabledOverlayColor(lookup.getColor("slotDisabledOverlayColor", fb.slotDisabledOverlayColor))
        }

        private fun decodeButton(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .longPressThresholdMs(
                    lookup.getNumber("longPressThresholdMs", fb.longPressThresholdMs().toDouble()).toLong(),
                )
                .doubleClickThresholdMs(
                    lookup.getNumber("doubleClickThresholdMs", fb.doubleClickThresholdMs().toDouble()).toLong(),
                )
                .textPaddingY(lookup.getNumber("textPaddingY", fb.textPaddingY.toDouble()).toFloat())
                .textBaselineY(lookup.getNumber("textBaselineY", fb.textBaselineY.toDouble()).toFloat())
                .buttonCornerRadius(lookup.getNumber("buttonCornerRadius", fb.buttonCornerRadius.toDouble()).toFloat())
                .buttonIconSpacing(lookup.getNumber("buttonIconSpacing", fb.buttonIconSpacing.toDouble()).toFloat())
                .buttonElevationDefault(
                    lookup.getNumber("buttonElevationDefault", fb.buttonElevationDefault.toDouble()).toFloat(),
                )
                .buttonElevationHover(
                    lookup.getNumber("buttonElevationHover", fb.buttonElevationHover.toDouble()).toFloat(),
                )
                .buttonElevationPressed(
                    lookup.getNumber("buttonElevationPressed", fb.buttonElevationPressed.toDouble()).toFloat(),
                )
                .buttonTextTransform(
                    lookup.getString("buttonTextTransform", fb.buttonTextTransform) ?: fb.buttonTextTransform,
                )
        }

        private fun decodeTooltip(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .tooltipDelayMs(lookup.getNumber("tooltipDelayMs", fb.tooltipDelayMs.toDouble()).toLong())
                .tooltipBackgroundColor(lookup.getColor("tooltipBackgroundColor", fb.tooltipBackgroundColor))
                .tooltipTextColor(lookup.getColor("tooltipTextColor", fb.tooltipTextColor))
                .tooltipCornerRadius(
                    lookup.getNumber("tooltipCornerRadius", fb.tooltipCornerRadius.toDouble()).toFloat(),
                )
                .tooltipPaddingX(lookup.getNumber("tooltipPaddingX", fb.tooltipPaddingX.toDouble()).toFloat())
                .tooltipPaddingY(lookup.getNumber("tooltipPaddingY", fb.tooltipPaddingY.toDouble()).toFloat())
                .tooltipLineSpacing(lookup.getNumber("tooltipLineSpacing", fb.tooltipLineSpacing.toDouble()).toFloat())
        }

        /** 解码控件覆盖兜底 token（原硬编码字面量），缺失回退 fb。 */
        private fun decodeControlOverrides(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .progressBarNotchColor(lookup.getColor("progressBarNotchColor", fb.progressBarNotchColor))
                .itemStackBackgroundColor(lookup.getColor("itemStackBackgroundColor", fb.itemStackBackgroundColor))
                .itemStackBackgroundInnerColor(
                    lookup.getColor("itemStackBackgroundInnerColor", fb.itemStackBackgroundInnerColor),
                )
                .itemStackHoverColor(lookup.getColor("itemStackHoverColor", fb.itemStackHoverColor))
                .dropdownOptionHoverColor(lookup.getColor("dropdownOptionHoverColor", fb.dropdownOptionHoverColor))
                .slotBackgroundOuterColor(lookup.getColor("slotBackgroundOuterColor", fb.slotBackgroundOuterColor))
                .slotBackgroundInnerColor(lookup.getColor("slotBackgroundInnerColor", fb.slotBackgroundInnerColor))
        }

        /** 解码装饰效果 token（默认全关），缺失回退 fb。 */
        private fun decodeDecorationEffects(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            b
                .glowColor(lookup.getColor("glowColor", fb.glowColor))
                .glowIntensity(lookup.getNumber("glowIntensity", fb.glowIntensity.toDouble()).toFloat())
                .glowSize(lookup.getNumber("glowSize", fb.glowSize.toDouble()).toFloat())
                .frameStrokeColor(lookup.getColor("frameStrokeColor", fb.frameStrokeColor))
                .frameStrokeWidth(lookup.getNumber("frameStrokeWidth", fb.frameStrokeWidth.toDouble()).toFloat())
                .scanlineColor(lookup.getColor("scanlineColor", fb.scanlineColor))
                .scanlineDensity(lookup.getNumber("scanlineDensity", fb.scanlineDensity.toDouble()).toFloat())
                .animDurationEnterMs(
                    lookup.getNumber("animDurationEnterMs", fb.animDurationEnterMs.toDouble()).toLong(),
                )
                .animDurationExitMs(lookup.getNumber("animDurationExitMs", fb.animDurationExitMs.toDouble()).toLong())
                .animDurationStaggerMs(
                    lookup.getNumber("animDurationStaggerMs", fb.animDurationStaggerMs.toDouble()).toLong(),
                )
        }

        /**
         * 解码 toast 样式表：扁平 dotted key + 嵌套展开。
         * 命名样式优先以 fb.toastStyles 中同名 entry 为兜底，全新命名样式以 fb.defaultToastStyle 为兜底。
         */
        private fun decodeToast(b: Builder, lookup: ThemeLookup, fb: ControlTheme) {
            val defaultStyle = decodeToastStyle(lookup, "defaultToastStyle", fb.defaultToastStyle)
            b.defaultToastStyle(defaultStyle)
            // 兼容 fallback 主题已有的命名样式 + lookup 中新声明的命名样式，按 fallback 优先填充
            val names = LinkedHashSet(fb.toastStyles.keys)
            names.addAll(lookup.childKeys("toastStyle"))
            // 先清空再逐项写入，避免与 fb 共享同名后污染
            b.clearToastStyles()
            for (name in names) {
                val styleFb = fb.toastStyles[name] ?: defaultStyle
                val decoded = decodeToastStyle(lookup, "toastStyle.$name", styleFb)
                b.toastStyle(name, decoded)
            }
        }

        /** 单条 ToastStyle 的字段级 lookup 解码，缺失字段回退到 fb 对应字段。 */
        private fun decodeToastStyle(lookup: ThemeLookup, prefix: String, fb: ToastStyle): ToastStyle =
            ToastStyle.builder()
                .anchor(lookup.getString("$prefix.anchor", fb.anchor) ?: fb.anchor)
                .offsetX(lookup.getNumber("$prefix.offsetX", fb.offsetX.toDouble()).toFloat())
                .offsetY(lookup.getNumber("$prefix.offsetY", fb.offsetY.toDouble()).toFloat())
                .width(lookup.getNumber("$prefix.width", fb.width.toDouble()).toFloat())
                .height(lookup.getNumber("$prefix.height", fb.height.toDouble()).toFloat())
                .gap(lookup.getNumber("$prefix.gap", fb.gap.toDouble()).toFloat())
                .backgroundColor(lookup.getColor("$prefix.backgroundColor", fb.backgroundColor))
                .textColor(lookup.getColor("$prefix.textColor", fb.textColor))
                .borderColor(lookup.getColor("$prefix.borderColor", fb.borderColor))
                .borderWidth(lookup.getNumber("$prefix.borderWidth", fb.borderWidth.toDouble()).toFloat())
                .cornerRadius(lookup.getNumber("$prefix.cornerRadius", fb.cornerRadius.toDouble()).toFloat())
                .shadowColor(lookup.getColor("$prefix.shadowColor", fb.shadowColor))
                .shadowBlur(lookup.getNumber("$prefix.shadowBlur", fb.shadowBlur.toDouble()).toFloat())
                .shadowOffsetX(lookup.getNumber("$prefix.shadowOffsetX", fb.shadowOffsetX.toDouble()).toFloat())
                .shadowOffsetY(lookup.getNumber("$prefix.shadowOffsetY", fb.shadowOffsetY.toDouble()).toFloat())
                .textSize(lookup.getNumber("$prefix.textSize", fb.textSize.toDouble()).toFloat())
                .textPaddingX(lookup.getNumber("$prefix.textPaddingX", fb.textPaddingX.toDouble()).toFloat())
                .textPaddingY(lookup.getNumber("$prefix.textPaddingY", fb.textPaddingY.toDouble()).toFloat())
                .fadeInMs(lookup.getNumber("$prefix.fadeInMs", fb.fadeInMs.toDouble()).toLong())
                .fadeOutMs(lookup.getNumber("$prefix.fadeOutMs", fb.fadeOutMs.toDouble()).toLong())
                .build()

        /**
         * 默认兜底主题实例：数值与现行渲染器硬编码常量一一对应，保持视觉零回归。
         * 内建 4 个 toast 样式 + bottomRight 紧凑样式 + Slot 背景灰兜底。
         */
        private val LEGACY_FALLBACK: ControlTheme = builder()
            .primary(0xFF4CAF50.toInt())
            .onPrimary(0xFFFFFFFF.toInt())
            .primaryContainer(0xFF4CAF50.toInt())
            .onPrimaryContainer(0xFFFFFFFF.toInt())
            .inversePrimary(0xFF388E3C.toInt())
            .secondary(0xFFFFD54F.toInt())
            .onSecondary(0xFF000000.toInt())
            .secondaryContainer(0xFFFFD54F.toInt())
            .onSecondaryContainer(0xFF000000.toInt())
            .tertiary(0xFF42A5F5.toInt())
            .onTertiary(0xFFFFFFFF.toInt())
            .tertiaryContainer(0xFF42A5F5.toInt())
            .onTertiaryContainer(0xFFFFFFFF.toInt())
            .error(0xFFCC4444.toInt())
            .onError(0xFFFFFFFF.toInt())
            .errorContainer(0xFF93000A.toInt())
            .onErrorContainer(0xFFFFFFFF.toInt())
            .successColor(0xFF4CAF50.toInt())
            .onSuccess(0xFFFFFFFF.toInt())
            .warningColor(0xFFFF9800.toInt())
            .onWarning(0xFFFFFFFF.toInt())
            .infoColor(0xFF2196F3.toInt())
            .onInfo(0xFFFFFFFF.toInt())
            .background(0xFF16181D.toInt())
            .onBackground(0xFFFFFFFF.toInt())
            .surface(0xFF2A2A2A.toInt())
            .onSurface(0xFFFFFFFF.toInt())
            .surfaceVariant(0xFF404040.toInt())
            .onSurfaceVariant(0xFF888888.toInt())
            .surfaceContainerLowest(0xFF1A1A1A.toInt())
            .surfaceContainerLow(0xFF1F1F1F.toInt())
            .surfaceContainer(0xFF262B34.toInt())
            .surfaceContainerHigh(0xFF404040.toInt())
            .surfaceContainerHighest(0xFF555555.toInt())
            .surfaceTint(0xFF4CAF50.toInt())
            .inverseSurface(0xFFFFFFFF.toInt())
            .inverseOnSurface(0xFF1A1A1A.toInt())
            .outline(0xFF888888.toInt())
            .outlineVariant(0xFF666666.toInt())
            .shadow(0xFF000000.toInt())
            .scrim(0xFF000000.toInt())
            .toastStyle(
                "info",
                ToastStyle.builder().backgroundColor(0xFF2196F3.toInt()).textColor(0xFFFFFFFF.toInt()).build(),
            )
            .toastStyle(
                "success",
                ToastStyle.builder().backgroundColor(0xFF42A5F5.toInt()).textColor(0xFFFFFFFF.toInt()).build(),
            )
            .toastStyle(
                "warning",
                ToastStyle.builder().backgroundColor(0xFF93000A.toInt()).textColor(0xFFFFFFFF.toInt()).build(),
            )
            .toastStyle(
                "error",
                ToastStyle.builder().backgroundColor(0xFFCC4444.toInt()).textColor(0xFFFFFFFF.toInt()).build(),
            )
            .toastStyle(
                "bottomRight",
                ToastStyle.builder()
                    .anchor("bottom-right")
                    .offsetX(16f)
                    .offsetY(16f)
                    .width(180f)
                    .height(24f)
                    .gap(4f)
                    .textSize(0.85f)
                    .textPaddingX(6f)
                    .textPaddingY(7f)
                    .cornerRadius(4f)
                    .backgroundColor(0xCC1A1A1A.toInt())
                    .textColor(0xFFFFFFFF.toInt())
                    .build(),
            )
            .slotBackgroundOuterColor(0xFF8B8B8B.toInt())
            .slotBackgroundInnerColor(0xFF373737.toInt())
            .build()
    }
}
